// The Cleanup issue card — one sentence, one picture, one button.
//
// Built from docs/archive/mockups/cleanup-pane-redesign-v1.html (approved
// 2026-09-15) and rendered wherever the in-record drawer finds an issue
// whose detector DECLARED its fix (issue.fix — see shared/cleanup/fix.ts)
// or whose fix can be inferred (fix-shape.ts). Four zones, top to bottom:
//
//   WHAT'S WRONG   the short title, the detector's question, and a
//                  from → to picture that carries the evidence a person
//                  would otherwise read in a paragraph;
//   HOW IT HAPPENED one line, its own slot;
//   THE FIX        one primary button that names the change and the
//                  destination, with the confidence as a word, and what
//                  travels with the record under it;
//   WHY WE THINK SO folded — the reasons, the records, the numbers.
//
// It executes the move through the SAME endpoint the record page's own
// "Move to household" button uses (POST /api/records/:kind/:id/
// move-household: scope-gated, audited, one bulk action, undoable via
// POST /api/archive/undo). Nothing here is a new write path.
//
// What it deliberately leaves to the existing DecideRail, reachable from
// "More options…": assign, waiting-on-someone, notes, and every bespoke
// flow. Rebuilding those forms here would be a second copy of each.

import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  ArrowRightLeft,
  Check,
  ChevronDown,
  ExternalLink,
  FileText,
  Home,
  Info,
  Search,
  User,
  X,
} from "lucide-react";

import {
  api,
  ApiError,
  type AssignableUser,
  type DuplicatePerson,
  type DuplicateHousehold,
  type MissingDocumentsState,
  apiV11,
  type CleanupIssueSummary,
  type HouseholdContact,
  type HouseholdListItem,
  type JudgmentItemType,
  type MyJudgmentItem,
  type MyJudgmentItemNumbers,
  type ServiceRecordPerson,
} from "../api";
import { useToast } from "../components/Toast";
import { OpportunityOutcomeModal } from "../components/OpportunityOutcomeModal";
import { contactMoveSource } from "./contact-move-predicate";
import { realtyLinkSource } from "./realty-link-predicate";
import {
  ivansCreatePolicySource,
  ivansSuggestedClientName,
  ivansSuggestedHousehold,
  ivansSuggestedPolicyType,
} from "./ivans-create-policy-predicate";
import { stubTwinChoices } from "./stub-twin-predicate";
import { AddInsurancePolicyInlineForm } from "../components/household-add-record/AddInsurancePolicyForm";
import { NumbersEditor } from "../data-needed/judgment-editors";
import {
  RecordPeopleControl,
  serviceRecordPeopleAdapter,
  workflowRecordPeopleAdapter,
} from "../components/service-records/RecordPeopleControl";
import { personRoleNoun } from "@shared/service-records/people-labels";
import type { TypeItInRecordKind } from "@shared/cleanup/fix";
import type { RecordRef } from "./cleanup-records";
import {
  confidencePill,
  movableNoun,
  type IssueRef,
  type ResolvedFix,
  type ResolvedKeepOne,
  type ResolvedMove,
  type ResolvedAddPolicy,
  type ResolvedFillOne,
  type ResolvedSamePerson,
  type ResolvedChooseAnswer,
  type ResolvedSameHousehold,
  type ResolvedFindHousehold,
  type ResolvedRetention,
  type ResolvedMissingDocuments,
  type ResolvedRewin,
  type ResolvedOpenPage,
  type ResolvedOpenPicker,
  type ResolvedPickOne,
  type ResolvedTypeItIn,
  type ResolvedTypeItInFields,
  type ResolvedSplitAccounts,
  resolveFix,
} from "./fix-shape";
import {
  answersFor,
  dollarsWhole,
  initialChoice,
  newHouseholdLabel,
  rowState,
  summarize,
  type SplitRowChoice,
} from "./split-accounts";
import {
  ANSWER_WRITERS,
  currentAsControlValue,
  currentAsWords,
} from "@shared/cleanup/answer-writers";
import type {
  CleanupAskedField,
  CleanupChooseAnswer,
  CleanupFieldsAlternative,
  CleanupSplitAccount,
} from "@shared/cleanup/fix";
import { MOVE_REASON_OPTIONS } from "./move-reasons";
import { RetentionDispositionPanel } from "./RetentionDispositionPanel";
import { recordDeepLink } from "./record-links";
import { parseSalePriceInput } from "./realty-price-issue";
import "./issue-card.css";

export interface IssueCardProps {
  issue: CleanupIssueSummary;
  fix: ResolvedFix;
  /** A record chip was clicked: open it in the record drawer. */
  onOpenRecord: (r: RecordRef) => void;
  /** The issue left the open set (moved, dismissed, snoozed, seen). */
  onResolved: () => void;
  /** Reveal the full decide rail under the card (assign, waiting, note…). */
  onToggleMore: () => void;
  moreOpen: boolean;
  /** Open the field-by-field contact merge screen (the host owns it, and
   *  marks the issue resolved when the merge lands). */
  onRequestFieldMerge?: (
    members: RecordRef[],
    matchType: "email" | "phone" | "name_zip",
    matchValue: string,
  ) => void;
  /** Open the field-by-field household merge screen (the host owns it,
   *  and marks the issue resolved when the merge lands). */
  onRequestHouseholdFieldMerge?: (
    members: RecordRef[],
    opts?: { defaultSurvivorId?: string; initialReason?: string; samePersonConfirmed?: boolean },
  ) => void;
  /** Open the search-and-merge screen for a household whose family is
   *  not yet known (the host owns it, and resolves the issue on merge). */
  onRequestHouseholdSearchMerge?: (source: RecordRef) => void;
  /** The existing pickers an "open-picker" card opens (the host owns each
   *  screen and resolves the item when its own action lands). */
  onRequestContactMove?: (source: RecordRef) => void;
  onRequestRealtyLink?: (source: RecordRef) => void;
  onRequestCreatePolicy?: (
    source: RecordRef,
    suggested: { first: string; last: string; policyType: string; household: { id: string; name: string } | null },
  ) => void;
}

type Stage = { kind: "idle" } | { kind: "confirm"; to: IssueRef } | { kind: "search" };

const ICON = { size: 16, strokeWidth: 1.5 } as const;
const ICON_XS = { size: 14, strokeWidth: 1.5 } as const;

function Pill({ tone, children }: { tone: "sure" | "check" | "info"; children: React.ReactNode }) {
  return <span className={`ic-pill ic-pill--${tone}`}>{children}</span>;
}

function RecordIcon({ kind }: { kind: string }) {
  if (kind === "household") return <Home {...ICON_XS} aria-hidden />;
  if (kind === "contact") return <User {...ICON_XS} aria-hidden />;
  return <FileText {...ICON_XS} aria-hidden />;
}

/** The person the issue names, when it names one — used for the
 *  "No Jonathan here" / "Jonathan lives here" lines in the picture. */
function personFirstName(issue: CleanupIssueSummary): string | null {
  const c = issue.affectedRecords.find((r) => r.kind === "contact");
  if (!c) return null;
  const full = c.label.split(" · ")[0]?.trim() ?? "";
  const first = full.split(/\s+/)[0] ?? "";
  return first || null;
}

function whyHint(fix: ResolvedFix): string {
  if (fix.kind === "acknowledge") return "for your information";
  if (fix.kind === "open-page") return "the answer is on that page";
  if (fix.kind === "split-accounts") return "the titles name different owners";
  if (fix.kind === "type-it-in" || fix.kind === "type-it-in-fields") return "only you know this";
  if (fix.kind === "pick-one") return "a decision only you can make";
  if (fix.kind === "add-policy") return "only you can say whose policy it is";
  if (fix.kind === "fill-one") return "only you know this";
  if (fix.kind === "choose-answer") return "only you can say which it is";
  if (fix.kind === "find-household") return "only you know whose it is";
  if (fix.kind === "retention") return "keeping it is always safe; destroying is permanent";
  if (fix.kind === "missing-documents") return "only you know whether it applies here";
  if (fix.kind === "rewin") return "only you know whether it really closed";
  if (fix.kind === "open-picker") return "only you know which one it is";
  if (fix.kind === "same-household" && fix.variant === "import-stub") return "the data can't settle it";
  if (fix.kind === "same-household") return fix.likelySame ? "they look like one family" : "they may be different families";
  if (fix.kind === "same-person") return fix.likelySame ? "they look like one person" : "they may be different people";
  if (fix.kind === "keep-one") {
    return fix.keeperId ? "one looks like the real one" : "nothing settles which is real";
  }
  switch (fix.confidence) {
    case "confident":
      return "two facts agree";
    case "check-first":
      return "worth a look before moving";
    default:
      return "only you can say";
  }
}

export function IssueCard(props: IssueCardProps) {
  const { fix } = props;
  if (fix.kind === "split-accounts") return <SplitAccountsCard {...props} fix={fix} />;
  if (fix.kind === "move-record") return <MoveCard {...props} fix={fix} />;
  if (fix.kind === "keep-one") return <KeepOneCard {...props} fix={fix} />;
  if (fix.kind === "type-it-in") return <TypeItInCard {...props} fix={fix} />;
  if (fix.kind === "type-it-in-fields") return <FieldsCard {...props} fix={fix} />;
  if (fix.kind === "pick-one") return <PickOneCard {...props} fix={fix} />;
  if (fix.kind === "add-policy") return <AddPolicyCard {...props} fix={fix} />;
  if (fix.kind === "fill-one") return <FillOneCard {...props} fix={fix} />;
  if (fix.kind === "same-person") return <SamePersonCard {...props} fix={fix} />;
  if (fix.kind === "choose-answer") return <ChooseAnswerCard {...props} fix={fix} />;
  if (fix.kind === "same-household") return <SameHouseholdCard {...props} fix={fix} />;
  if (fix.kind === "find-household") return <FindHouseholdCard {...props} fix={fix} />;
  if (fix.kind === "retention") return <RetentionCard {...props} fix={fix} />;
  if (fix.kind === "missing-documents") return <MissingDocumentsCard {...props} fix={fix} />;
  if (fix.kind === "rewin") return <RewinCard {...props} fix={fix} />;
  if (fix.kind === "open-picker") return <OpenPickerCard {...props} fix={fix} />;
  if (fix.kind === "open-page") return <OpenPageCard {...props} fix={fix} />;
  return <AcknowledgeCard {...props} />;
}

// ─── Shared pieces ──────────────────────────────────────────────────

function WhyWeThinkSo({
  issue,
  fix,
  onOpenRecord,
}: {
  issue: CleanupIssueSummary;
  fix: ResolvedFix;
  onOpenRecord: (r: RecordRef) => void;
}) {
  const reasons = (issue.flagReasons ?? []).filter(
    (r): r is string => typeof r === "string" && r.trim().length > 0,
  );
  const found = issue.firstDetectedAt ? issue.firstDetectedAt.slice(0, 10) : null;
  return (
    <details className="ic-why">
      <summary>
        <ChevronDown {...ICON} aria-hidden />
        Why we think so <span className="ic-why-mut">· {whyHint(fix)}</span>
      </summary>
      {reasons.length > 0 ? (
        <ul>
          {reasons.map((r) => (
            <li key={r}>
              <span className="ic-dot" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ic-meta">{issue.flagExplanation}</p>
      )}
      <div className="ic-recs">
        {issue.affectedRecords.map((r) => {
          const href = recordDeepLink(r.kind, r.id);
          return (
            <span key={`${r.kind}:${r.id}`} style={{ display: "inline-flex" }}>
              <button
                type="button"
                className="ic-recl"
                onClick={() => onOpenRecord({ kind: r.kind, id: r.id, label: r.label })}
                title="Open in the record inspector"
              >
                <RecordIcon kind={r.kind} />
                {r.label}
              </button>
              {href && (
                <a
                  className="ic-recl"
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the full page in a new tab"
                  aria-label={`Open ${r.label} in a new tab`}
                  style={{ marginLeft: -1, padding: "0 8px" }}
                >
                  <ExternalLink {...ICON_XS} aria-hidden />
                </a>
              )}
            </span>
          );
        })}
      </div>
      <p className="ic-meta">
        {issue.confidence}% match · {issue.tier} tier
        {found ? ` · first seen ${found}` : ""}
      </p>
    </details>
  );
}

function CardFooter({
  issueId,
  busy,
  onSnooze,
  onDismiss,
  onResolved,
  onToggleMore,
  moreOpen,
}: {
  issueId: string;
  busy: boolean;
  onSnooze: () => void;
  onDismiss: () => void;
  onResolved: () => void;
  onToggleMore: () => void;
  moreOpen: boolean;
}) {
  // The three ways out that are never a dead end (CLAUDE.md: "No dead
  // ends"): set it aside, say it is not an issue, or hand it to someone.
  // They sat behind "More options…" until 2026-09-30, so the card the
  // mockup drew with all three on it (data-needed-overhaul-mockup-v1)
  // hid two of them, and N / L (the keys the Data Needed page documents)
  // reached nothing on a declared-fix card.
  const toast = useToast();
  const [handOpen, setHandOpen] = useState(false);
  const [people, setPeople] = useState<AssignableUser[] | null>(null);
  const [pickedId, setPickedId] = useState("");
  const [handBusy, setHandBusy] = useState(false);
  const [handErr, setHandErr] = useState<string | null>(null);

  // The roster loads the first time "Hand to…" opens, not with every
  // card: a list of 50 cards would otherwise ask for it 50 times.
  useEffect(() => {
    if (!handOpen || people !== null) return;
    let live = true;
    api
      .listAssignableUsers()
      .then((r) => {
        if (live) setPeople(r.users);
      })
      .catch(() => {
        if (live) setPeople([]);
      });
    return () => {
      live = false;
    };
  }, [handOpen, people]);

  async function handOver() {
    if (!pickedId || handBusy) return;
    setHandBusy(true);
    setHandErr(null);
    try {
      await api.assignCleanupIssue(issueId, pickedId);
      const who = people?.find((u) => u.id === pickedId)?.name ?? "them";
      toast.success(`Handed to ${who}. It's in their queue now.`);
      onResolved();
    } catch (e) {
      setHandErr(e instanceof ApiError ? e.message : "Couldn't hand it over. Try again.");
    } finally {
      setHandBusy(false);
    }
  }

  return (
    <>
      <footer className="ic-foot">
        <button type="button" className="ic-lnk" data-action="later" disabled={busy} onClick={onSnooze}>
          Snooze 24h
        </button>
        <button
          type="button"
          className="ic-lnk"
          data-action="not"
          disabled={busy}
          title="Not an issue: dismiss it. Reopen any time from Recently resolved."
          onClick={onDismiss}
        >
          Not an issue
        </button>
        <button
          type="button"
          className="ic-lnk"
          aria-expanded={handOpen}
          disabled={busy}
          onClick={() => setHandOpen((o) => !o)}
        >
          Hand to…
        </button>
        <button type="button" className="ic-lnk" onClick={onToggleMore} aria-expanded={moreOpen}>
          {moreOpen ? "Fewer options" : "More options…"}
        </button>
        <span className="ic-foot-sp" />
      </footer>
      {handOpen && (
        <div className="ic-hand">
          {people === null ? (
            <p className="ic-note">Loading the team…</p>
          ) : people.length === 0 ? (
            <p className="ic-note">Couldn't load the team. Use "More options…" to assign it.</p>
          ) : (
            <>
              <label className="ic-hand-k" htmlFor={`ic-hand-${issueId}`}>
                Hand it to
              </label>
              <select
                id={`ic-hand-${issueId}`}
                className="ic-input ic-hand-sel"
                value={pickedId}
                disabled={handBusy}
                onChange={(e) => setPickedId(e.target.value)}
              >
                <option value="">Choose a teammate…</option>
                {people.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name ?? u.email}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="ic-btn ic-btn--ghost ic-btn--auto"
                disabled={!pickedId || handBusy}
                onClick={() => void handOver()}
              >
                Hand over
              </button>
            </>
          )}
          {handErr && <p className="ic-err">{handErr}</p>}
        </div>
      )}
    </>
  );
}

/** Snooze / dismiss / acknowledge through POST /api/cleanup/decide —
 *  the verbs that touch only the issue, never the record. */
function useDisposition(issue: CleanupIssueSummary, onResolved: () => void) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function decide(verb: "dismiss" | "snooze" | "acknowledge", reasonText?: string) {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb,
        ...(reasonText ? { reason_text: reasonText } : {}),
        ...(verb !== "acknowledge" && issue.recommendation ? { overrode_recommendation: true } : {}),
      });
      // "Not an issue" is the one disposition a mis-tap should not make
      // permanent: say so and offer the one-click way back (the same
      // reopen the 60-second undo uses).
      // (Optional-chained: the undo handle is a courtesy, and a response
      // without one must never keep the card from leaving the list.)
      if (verb === "dismiss" && res?.undo?.available) {
        const undo = res.undo;
        toast.success("Marked not an issue.", {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                .then(() => toast.success("Back on your list."))
                .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
            },
          },
        });
      }
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through — try again.");
    } finally {
      setBusy(false);
    }
  }
  return { busy, err, decide };
}

// ─── Move a record ──────────────────────────────────────────────────

function MoveCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedMove }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [moving, setMoving] = useState(false);
  const [moveErr, setMoveErr] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState<string>("data_quality_cleanup");
  const [reasonText, setReasonText] = useState("");

  const busy = moving || disposition.busy;
  const err = moveErr ?? disposition.err;
  const pill = confidencePill(fix.confidence);
  const person = personFirstName(issue);
  const noun = movableNoun(fix.record.kind);

  async function move(to: IssueRef) {
    if (moving) return;
    setMoving(true);
    setMoveErr(null);
    try {
      const res = await api.moveRecordToHousehold(fix.record.kind, fix.record.id, {
        target_household_id: to.id,
        reason_code: reasonCode,
        ...(reasonText.trim() ? { reason: reasonText.trim() } : {}),
      });
      const went = [
        res.care_plans_moved
          ? `${res.care_plans_moved} care plan${res.care_plans_moved === 1 ? "" : "s"}`
          : null,
        res.processing_moved
          ? `${res.processing_moved} processing record${res.processing_moved === 1 ? "" : "s"}`
          : null,
      ].filter((s): s is string => s != null);
      toast.success(
        `Moved to "${to.label}".${went.length > 0 ? ` ${went.join(" and ")} went with it.` : ""}`,
        {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .archiveUndo(res.bulk_action_id)
                .then(() => toast.success("Move undone."))
                .catch(() => toast.error("Undo failed — the record may have changed."));
            },
          },
        },
      );
      try {
        await api.markCleanupIssueResolved(issue.id);
      } catch {
        /* the ~6h sweep will resolve it; don't block the UI */
      }
      onResolved();
    } catch (e) {
      setMoveErr(e instanceof ApiError ? e.message : "That didn't go through — try again.");
    } finally {
      setMoving(false);
    }
  }

  // ── Confirm ────────────────────────────────────────────────────────
  if (stage.kind === "confirm") {
    const to = stage.to;
    const needsText = reasonCode === "other_with_text" && reasonText.trim().length === 0;
    return (
      <article className="ic-card">
        <div className="ic-eyebrow">Confirm the move</div>
        <h2 className="ic-q">
          {fix.record.label} moves to {to.label}.
        </h2>
        <div className="ic-cmp">
          <div className="ic-hh ic-hh--from">
            <div className="ic-hh-k">Now</div>
            <div className="ic-hh-n">{fix.from?.label ?? "Where it sits today"}</div>
            {fix.from?.members_label && <div className="ic-hh-m">{fix.from.members_label}</div>}
          </div>
          <ArrowRight size={22} strokeWidth={1.5} aria-hidden />
          <div className="ic-hh ic-hh--to">
            <div className="ic-hh-k">After</div>
            <div className="ic-hh-n">{to.label}</div>
            {to.members_label && <div className="ic-hh-m">{to.members_label}</div>}
          </div>
        </div>
        <ul className="ic-kv">
          <li>
            <span className="ic-kv-k">Goes with it</span>
            <span>
              {fix.record.kind === "service_record"
                ? "Its Ongoing Care plans and Processing records, and its client status is recomputed on both households."
                : "Its client status is recomputed on both households."}
            </span>
          </li>
          <li>
            <span className="ic-kv-k">Stays put</span>
            <span>Nothing. The {noun} keeps its history, dates and people.</span>
          </li>
          <li>
            <span className="ic-kv-k">Reason</span>
            <span>
              <select
                id={`ic-reason-${issue.id}`}
                className="ic-sel"
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                disabled={busy}
                aria-label="Reason for the move (recorded for the undo trail)"
              >
                {MOVE_REASON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {reasonCode === "other_with_text" && (
                <input
                  id={`ic-reason-text-${issue.id}`}
                  className="ic-input"
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="One line on why"
                  disabled={busy}
                />
              )}
            </span>
          </li>
        </ul>
        <div className="ic-btns">
          <button
            type="button"
            className="ic-btn ic-btn--primary"
            disabled={busy || needsText}
            onClick={() => void move(to)}
          >
            <Check {...ICON} aria-hidden /> {moving ? "Moving…" : "Move it"}
          </button>
          <button
            type="button"
            className="ic-btn ic-btn--ghost"
            disabled={busy}
            onClick={() => setStage({ kind: "idle" })}
          >
            Back
          </button>
        </div>
        <p className="ic-note">
          Undo for 60 seconds after. Every change shows on the History tab of both households.
        </p>
        {err && <p className="ic-err">{err}</p>}
      </article>
    );
  }

  // ── Search ─────────────────────────────────────────────────────────
  if (stage.kind === "search") {
    return (
      <HouseholdPicker
        issue={issue}
        fix={fix}
        busy={busy}
        onPick={(to) => setStage({ kind: "confirm", to })}
        onBack={() => setStage({ kind: "idle" })}
      />
    );
  }

  // ── Idle ───────────────────────────────────────────────────────────
  return (
    <article className="ic-card">
      {/* The headline is the detector's own question, in sentence case —
          the mockup's "Card title: the detector's question, cut to one
          sentence". The short label (fix.title) is what the row list
          above shows, so printing it here too would say it twice. */}
      <div className="ic-eyebrow">What's wrong</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>

      <div className="ic-flow">
        <div className="ic-hh ic-hh--from">
          <div className="ic-hh-k">Filed under</div>
          <div className="ic-hh-n">{fix.from?.label ?? "Where it sits today"}</div>
          {fix.from?.members_label && <div className="ic-hh-m">{fix.from.members_label}</div>}
          {person && fix.to && (
            <div className="ic-hh-v ic-hh-v--no">
              <X {...ICON_XS} aria-hidden /> No {person} here
            </div>
          )}
        </div>
        <div className="ic-flow-mid">
          <span className="ic-rec" title={fix.record.label}>
            <FileText {...ICON_XS} aria-hidden />
            <span>{fix.record.label}</span>
          </span>
          <ArrowRight size={22} strokeWidth={1.5} aria-hidden />
        </div>
        {fix.to ? (
          <div className="ic-hh ic-hh--to">
            <div className="ic-hh-k">Belongs with</div>
            <div className="ic-hh-n">{fix.to.label}</div>
            {fix.to.members_label && <div className="ic-hh-m">{fix.to.members_label}</div>}
            {person && (
              <div className="ic-hh-v ic-hh-v--yes">
                <Check {...ICON_XS} aria-hidden /> {person} lives here
              </div>
            )}
          </div>
        ) : (
          <div className="ic-hh">
            <div className="ic-hh-k">Belongs with</div>
            <div className="ic-hh-n">Not settled yet</div>
            <div className="ic-hh-m">Pick the household below.</div>
          </div>
        )}
      </div>

      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it happened.</b> {issue.howItHappened}
        </p>
      )}

      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        {fix.confidence === "check-first" && fix.to && (
          <div className="ic-check">
            <AlertTriangle {...ICON} aria-hidden />
            <div>
              <b>The evidence points one way, but it is not settled.</b> Open "Why we think so"
              before you move it.
            </div>
          </div>
        )}
        {fix.to ? (
          <button
            type="button"
            className="ic-btn ic-btn--primary"
            disabled={busy}
            onClick={() => setStage({ kind: "confirm", to: fix.to! })}
          >
            <ArrowRightLeft {...ICON} aria-hidden /> Move to {fix.to.label}
          </button>
        ) : (
          <button
            type="button"
            className="ic-btn ic-btn--primary"
            disabled={busy}
            onClick={() => setStage({ kind: "search" })}
          >
            <Search {...ICON} aria-hidden /> Find the right household…
          </button>
        )}
        <p className="ic-note">
          {fix.record.kind === "service_record"
            ? "Its Ongoing Care plans and Processing records go with it. Logged, and undoable for 60 seconds."
            : "Logged, and undoable for 60 seconds."}
        </p>
        <div className="ic-alt">
          {fix.to && (
            <button
              type="button"
              className="ic-btn ic-btn--ghost ic-btn--sm"
              disabled={busy}
              onClick={() => setStage({ kind: "search" })}
            >
              Different household…
            </button>
          )}
          <button
            type="button"
            className="ic-lnk ic-lnk--quiet"
            disabled={busy}
            onClick={() => void disposition.decide("dismiss")}
          >
            Not a problem, leave it
          </button>
        </div>
        {err && <p className="ic-err">{err}</p>}
      </div>

      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />

      <CardFooter
        issueId={issue.id}
        busy={busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

function HouseholdPicker({
  issue,
  fix,
  busy,
  onPick,
  onBack,
}: {
  issue: CleanupIssueSummary;
  fix: ResolvedMove;
  busy: boolean;
  onPick: (to: IssueRef) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const person = personFirstName(issue);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchErr(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({ q: q.trim(), limit: 20 });
        if (cancelled) return;
        setResults(
          res.households.filter((h) => h.id !== fix.from?.id && h.id !== fix.to?.id),
        );
        setSearchErr(null);
      } catch (e) {
        if (cancelled) return;
        setSearchErr(e instanceof ApiError ? e.message : "Search failed");
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, fix.from?.id, fix.to?.id]);

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">Pick the household</div>
      <h2 className="ic-q">Where does {fix.record.label} belong?</h2>
      <label className="ic-srch" htmlFor={`ic-search-${issue.id}`}>
        <Search {...ICON} aria-hidden />
        <input
          id={`ic-search-${issue.id}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search households by name…"
          autoFocus
        />
      </label>
      <ol className="ic-res">
        {fix.to && (
          <li className="is-sug">
            <div>
              <div className="ic-res-n">
                {fix.to.label} <Pill tone="info">Suggested</Pill>
              </div>
              {fix.to.members_label && <div className="ic-res-m">{fix.to.members_label}</div>}
              {person && (
                <div className="ic-res-v ic-res-v--yes">
                  <Check {...ICON_XS} aria-hidden /> {person} lives here
                </div>
              )}
            </div>
            <button
              type="button"
              className="ic-btn ic-btn--primary ic-btn--sm ic-btn--auto"
              disabled={busy}
              onClick={() => onPick(fix.to!)}
            >
              Move here
            </button>
          </li>
        )}
        {results.map((h) => (
          <li key={h.id}>
            <div>
              <div className="ic-res-n">{h.display_name}</div>
              <div className="ic-res-m">
                {[
                  h.members_label,
                  `${h.contact_count} ${h.contact_count === 1 ? "person" : "people"}`,
                  h.primary_city && h.primary_state ? `${h.primary_city}, ${h.primary_state}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button
              type="button"
              className="ic-btn ic-btn--ghost ic-btn--sm"
              disabled={busy}
              onClick={() =>
                onPick({
                  kind: "household",
                  id: h.id,
                  label: h.display_name,
                  members_label: h.members_label,
                })
              }
            >
              Move here
            </button>
          </li>
        ))}
        {fix.from && (
          <li className="is-cur">
            <div>
              <div className="ic-res-n">{fix.from.label}</div>
              <div className="ic-res-m">Where it is now</div>
            </div>
            <span />
          </li>
        )}
      </ol>
      {searching && <p className="ic-res-empty">Searching…</p>}
      {searchErr && <p className="ic-err">{searchErr}</p>}
      {!searching && q.trim().length >= 2 && results.length === 0 && !searchErr && (
        <p className="ic-res-empty">No other household matches "{q.trim()}".</p>
      )}
      {q.trim().length < 2 && !fix.to && (
        <p className="ic-res-empty">Type at least two letters of the family's name.</p>
      )}
      <button type="button" className="ic-lnk ic-lnk--quiet" onClick={onBack} disabled={busy}>
        Back
      </button>
    </article>
  );
}

// ─── Same person? (contact duplicates, side by side) ────────────────
//
// 2026-10-02. Six detectors flag contacts that might be one person. The
// card shows them side by side (GET /api/cleanup/issues/:id/people: the
// everyday fields and how much hangs off each record), then offers the two
// real answers. "Review the fields and merge…" opens the existing
// field-by-field merge screen, which pre-selects the most complete record
// to keep so protected fields are never lost (ClusterMergeOverlay, Baker
// 2026-05-18). "Different people" closes the item with that reason; it
// stays closed while the same people are flagged together.

type PersonField = "email" | "phone" | "birthday" | "household_name";

const PERSON_ROWS: readonly { key: PersonField; label: string }[] = [
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "birthday", label: "Birthday" },
  { key: "household_name", label: "Household" },
];

function sameAcross(people: readonly DuplicatePerson[], key: PersonField): boolean {
  const vals = people.map((p) => (p[key] ?? "").trim().toLowerCase());
  return vals.every((v) => v !== "" && v === vals[0]);
}

function SamePersonCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
  onRequestFieldMerge,
}: IssueCardProps & { fix: ResolvedSamePerson }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const [people, setPeople] = useState<DuplicatePerson[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getCleanupIssuePeople(issue.id)
      .then((r) => {
        if (!cancelled) setPeople(r.people.filter((p) => !p.gone));
      })
      .catch(() => {
        if (!cancelled) setPeople([]);
      });
    return () => {
      cancelled = true;
    };
  }, [issue.id]);

  function merge() {
    if (!onRequestFieldMerge) return;
    onRequestFieldMerge(
      fix.people.map((p) => ({ kind: "contact", id: p.id, label: p.label })),
      fix.matchType,
      fix.matchValue ?? issue.decisionQuestion,
    );
  }

  async function keepApart() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: "dismiss",
        overrode_recommendation: issue.recommendation != null,
        reason_text: "Different people — not a duplicate (kept separate)",
      });
      if (res?.undo?.available) {
        const undo = res.undo;
        toast.success("Kept apart: different people.", {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                .then(() => toast.success("Back on your list."))
                .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
            },
          },
        });
      }
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const mergeBtn = onRequestFieldMerge ? (
    <button
      key="merge"
      type="button"
      className={`ic-btn ${fix.likelySame ? "ic-btn--primary" : "ic-btn--ghost"}`}
      {...(fix.likelySame ? { "data-action": "primary" } : {})}
      disabled={busy}
      onClick={merge}
    >
      {fix.likelySame && <Check {...ICON} aria-hidden />}
      Same person: review the fields and merge…
    </button>
  ) : null;
  const apartBtn = (
    <button
      key="apart"
      type="button"
      className={`ic-btn ${fix.likelySame ? "ic-btn--ghost" : "ic-btn--primary"}`}
      {...(fix.likelySame ? {} : { "data-action": "primary" })}
      disabled={busy}
      onClick={() => void keepApart()}
    >
      {!fix.likelySame && <Check {...ICON} aria-hidden />}
      Different people: keep them apart
    </button>
  );

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">Same person?</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          Side by side <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        {people === null ? (
          <p className="ic-res-empty">Loading the people…</p>
        ) : people.length === 0 ? (
          <p className="ic-res-empty">Couldn't load the people. Open each one from Why we think so below.</p>
        ) : (
          <div className="ic-people" role="list">
            {people.map((p) => (
              <div key={p.id} className="ic-person" role="listitem">
                <button
                  type="button"
                  className="ic-person-n"
                  onClick={() => onOpenRecord({ kind: "contact", id: p.id, label: p.name })}
                >
                  {p.name}
                </button>
                <dl>
                  {PERSON_ROWS.map((row) => {
                    const v = p[row.key];
                    const same = sameAcross(people, row.key);
                    return (
                      <div key={row.key} className={same ? "is-same" : undefined}>
                        <dt>{row.label}</dt>
                        <dd>
                          {v ? (
                            <>
                              {same && <Check {...ICON_XS} aria-label="Same for all" />} {v}
                            </>
                          ) : (
                            <span className="ic-person-none">None</span>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                  <div>
                    <dt>On file</dt>
                    <dd>
                      {p.record_count} {p.record_count === 1 ? "record" : "records"} · {p.note_count}{" "}
                      {p.note_count === 1 ? "note" : "notes"}
                    </dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        )}
        <div className="ic-btns">{fix.likelySame ? [mergeBtn, apartBtn] : [apartBtn, mergeBtn]}</div>
        <p className="ic-note">
          Merging opens every field side by side first, keeps the most complete record and every contact method,
          and can be undone. "Different people" changes nothing on either record, and the OS stops asking while
          these same people are flagged together. Undo for 60 seconds.
        </p>
        {err && <p className="ic-err">{err}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Same household? (two households, side by side) ─────────────────
//
// 2026-10-02. household-twin flags two households that look like one
// family under names that don't quite match. The card shows them side by
// side (GET /api/cleanup/issues/:id/households: people, address, phones,
// emails, the companies that serve them, how much hangs off each), then
// offers the two real answers. "Review every field and merge…" opens the
// existing household merge screen with the detector's survivor picked
// (one click switches it); the merge moves every member, record and
// activity, deletes nothing and can be undone. "Different households"
// closes the item with that reason; it stays closed for that pair.

function sharedValues(lists: readonly (readonly string[])[]): Set<string> {
  const norm = (v: string) => v.trim().toLowerCase().replace(/[^a-z0-9@.]/g, "");
  const [first, ...rest] = lists.map((l) => new Set(l.map(norm).filter(Boolean)));
  const out = new Set<string>();
  if (!first) return out;
  for (const v of first) if (rest.length > 0 && rest.every((s) => s.has(v))) out.add(v);
  return out;
}

function HouseholdValues({ values, shared }: { values: readonly string[]; shared: Set<string> }) {
  if (values.length === 0) return <span className="ic-person-none">None</span>;
  return (
    <>
      {values.map((v, i) => {
        const isShared = shared.has(v.trim().toLowerCase().replace(/[^a-z0-9@.]/g, ""));
        return (
          <span key={v} className={isShared ? "ic-hh-v is-shared" : "ic-hh-v"}>
            {isShared && <Check {...ICON_XS} aria-label="Same in both" />}
            {v}
            {i < values.length - 1 ? ", " : ""}
          </span>
        );
      })}
    </>
  );
}

function SameHouseholdCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
  onRequestHouseholdFieldMerge,
}: IssueCardProps & { fix: ResolvedSameHousehold }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const [households, setHouseholds] = useState<DuplicateHousehold[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getCleanupIssueHouseholds(issue.id)
      .then((r) => {
        if (!cancelled) setHouseholds(r.households.filter((h) => !h.gone));
      })
      .catch(() => {
        if (!cancelled) setHouseholds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [issue.id]);

  const survivor = fix.households.find((h) => h.id === fix.survivorId) ?? null;
  const refs: RecordRef[] = fix.households.map((h) => ({ kind: "household", id: h.id, label: h.label }));

  function merge() {
    if (!onRequestHouseholdFieldMerge) return;
    onRequestHouseholdFieldMerge(refs, fix.survivorId ? { defaultSurvivorId: fix.survivorId } : undefined);
  }

  const stub = fix.variant === "import-stub";

  async function keepApart() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      // The import-placeholder answer is the side panel's exact call,
      // reason code included: these items are confirm-tier, and the
      // server refuses a confirm-tier decision without one.
      const res = await api.decideCleanupIssue(
        stub
          ? {
              issue_id: issue.id,
              verb: "dismiss",
              overrode_recommendation: false,
              reason_text:
                "Not the same family — the address is shared but the households are separate. The placeholder stays its own household.",
              reason_code: "data_quality_cleanup",
            }
          : {
              issue_id: issue.id,
              verb: "dismiss",
              overrode_recommendation: issue.recommendation != null,
              reason_text: "Different households — not a duplicate (kept separate)",
              // Some of these are confirm-tier (an empty household's merge
              // into its survivor); the server refuses those without a code.
              reason_code: "data_quality_cleanup",
            },
      );
      if (res?.undo?.available) {
        const undo = res.undo;
        toast.success("Kept apart: different households.", {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                .then(() => toast.success("Back on your list."))
                .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
            },
          },
        });
      } else {
        toast.success("Kept apart: different households.");
      }
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const list = households ?? [];
  const sharedPhones = sharedValues(list.map((h) => h.phones));
  const sharedEmails = sharedValues(list.map((h) => h.emails));
  const sharedAddress = sharedValues(list.map((h) => (h.address ? [h.address] : [])));
  const sharedCompanies = sharedValues(list.map((h) => h.companies));

  const mergeBtn = onRequestHouseholdFieldMerge ? (
    <button
      key="merge"
      type="button"
      className={`ic-btn ${fix.likelySame ? "ic-btn--primary" : "ic-btn--ghost"}`}
      {...(fix.likelySame ? { "data-action": "primary" } : {})}
      disabled={busy}
      onClick={merge}
    >
      {fix.likelySame && <Check {...ICON} aria-hidden />}
      Same household: review every field and merge…
    </button>
  ) : null;
  const apartBtn = (
    <button
      key="apart"
      type="button"
      className={`ic-btn ${fix.likelySame ? "ic-btn--ghost" : "ic-btn--primary"}`}
      {...(fix.likelySame ? {} : { "data-action": "primary" })}
      disabled={busy}
      onClick={() => void keepApart()}
    >
      {!fix.likelySame && <Check {...ICON} aria-hidden />}
      Different households: keep them apart
    </button>
  );

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">{fix.title}</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          Side by side <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        {households === null ? (
          <p className="ic-res-empty">Loading the households…</p>
        ) : list.length === 0 ? (
          <p className="ic-res-empty">Couldn't load the households. Open each one from Why we think so below.</p>
        ) : (
          <div className="ic-people" role="list">
            {list.map((h) => (
              <div key={h.id} className="ic-person" role="listitem">
                <button
                  type="button"
                  className="ic-person-n"
                  onClick={() => onOpenRecord({ kind: "household", id: h.id, label: h.name })}
                >
                  {h.name}
                </button>
                <dl>
                  <div>
                    <dt>People</dt>
                    <dd>
                      {h.members.length === 0 ? (
                        <span className="ic-person-none">No one</span>
                      ) : (
                        h.members.map((m) => m.name).join(", ")
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Address</dt>
                    <dd>
                      <HouseholdValues values={h.address ? [h.address] : []} shared={sharedAddress} />
                    </dd>
                  </div>
                  <div>
                    <dt>Phone</dt>
                    <dd>
                      <HouseholdValues values={h.phones} shared={sharedPhones} />
                    </dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>
                      <HouseholdValues values={h.emails} shared={sharedEmails} />
                    </dd>
                  </div>
                  <div>
                    <dt>Served by</dt>
                    <dd>
                      <HouseholdValues values={h.companies} shared={sharedCompanies} />
                    </dd>
                  </div>
                  <div>
                    <dt>On file</dt>
                    <dd>
                      {h.record_count} {h.record_count === 1 ? "record" : "records"} · {h.open_work_count} open
                    </dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        )}
        {stub ? (
          <div className="ic-answers">
            {onRequestHouseholdFieldMerge && (
              <>
                <div className="ic-answer">
                  <button
                    type="button"
                    className="ic-btn ic-btn--ghost"
                    disabled={busy}
                    onClick={() =>
                      onRequestHouseholdFieldMerge(refs, {
                        ...(fix.survivorId ? { defaultSurvivorId: fix.survivorId } : {}),
                        initialReason:
                          "Mortgage-import placeholder is the same person under a different surname — merged into their household.",
                        samePersonConfirmed: true,
                      })
                    }
                  >
                    Same person, different surname: review and merge…
                  </button>
                  <p className="ic-answer-c">
                    A married or maiden name. The loan moves onto the family, the placeholder goes away, and
                    the two person records become one (ticked on the next screen, so you can untick it).
                  </p>
                </div>
                <div className="ic-answer">
                  <button
                    type="button"
                    className="ic-btn ic-btn--ghost"
                    disabled={busy}
                    onClick={() =>
                      onRequestHouseholdFieldMerge(refs, {
                        ...(fix.survivorId ? { defaultSurvivorId: fix.survivorId } : {}),
                        initialReason:
                          "Mortgage-import placeholder is a second member of this household — merged in, both people kept.",
                      })
                    }
                  >
                    Different people, same household: put them together…
                  </button>
                  <p className="ic-answer-c">
                    A spouse or family member with another surname. Both people stay, in one household, and
                    the loan sits with the family.
                  </p>
                </div>
              </>
            )}
            <div className="ic-answer">
              <button type="button" className="ic-btn ic-btn--ghost" disabled={busy} onClick={() => void keepApart()}>
                Not the same family: leave them apart
              </button>
              <p className="ic-answer-c">
                A house that changed hands, or two unrelated people. Nothing merges and the OS stops asking.
                Undo for 60 seconds.
              </p>
            </div>
          </div>
        ) : (
          <div className="ic-btns">{fix.likelySame ? [mergeBtn, apartBtn] : [apartBtn, mergeBtn]}</div>
        )}
        {!stub && (
        <p className="ic-note">
          Merging opens every field side by side first
          {survivor ? `, with ${survivor.label} picked to keep (one click switches it)` : ""}. Every person,
          record and activity moves to the household you keep, nothing is deleted, and it can be undone.
          "Different households" changes nothing on either one, and the OS stops asking about this pair. Undo
          for 60 seconds.
        </p>
        )}
        {err && <p className="ic-err">{err}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Missing documents (upload each, or "not needed" with a reason) ─
//
// 2026-10-02. The old answer, "Attach the missing documents", attached
// nothing: it closed the item, and a closed item is never asked again.
// The card lists what the record is still missing (live, the queue's own
// rule), uploads each one filed under its type on the record (the same
// upload the Documents tab uses), and takes "Not needed because…" with a
// reason, effective at once (Grant: "immediate with a reason"). The item
// closes when nothing is left missing; the server re-checks before it does.

// ─── An existing picker, opened from the card ───────────────────────
//
// The answer is a choice made in a screen the OS already has (move a
// person into a household, link a Realty record, create a carrier's
// policy, merge a mortgage placeholder into one of its families, link a
// custodian's billed name). The card leads with it; the host owns the
// screen and resolves the item when that screen's own action lands.
function OpenPickerCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
  onRequestContactMove,
  onRequestRealtyLink,
  onRequestCreatePolicy,
  onRequestHouseholdFieldMerge,
}: IssueCardProps & { fix: ResolvedOpenPicker }) {
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const stub = fix.picker === "stub-family" ? stubTwinChoices(issue) : null;

  // The one button for the screens that need nothing chosen first.
  let open: (() => void) | null = null;
  if (fix.picker === "move-contact") {
    const src = contactMoveSource(issue);
    if (src && onRequestContactMove) open = () => onRequestContactMove(src);
  } else if (fix.picker === "link-realty") {
    const src = realtyLinkSource(issue);
    if (src && onRequestRealtyLink) open = () => onRequestRealtyLink(src);
  } else if (fix.picker === "create-policy") {
    const src = ivansCreatePolicySource(issue);
    if (src && onRequestCreatePolicy) {
      open = () => {
        const name = ivansSuggestedClientName(issue);
        onRequestCreatePolicy(src, {
          first: name.first,
          last: name.last,
          policyType: ivansSuggestedPolicyType(issue),
          household: ivansSuggestedHousehold(issue),
        });
      };
    }
  }

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">What's wrong</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it happened.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix · pick it <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        {fix.picker === "stub-family" && stub ? (
          <ul className="ic-keep ic-keep--plain">
            {stub.candidates.map((c) => (
              <li key={c.id}>
                <span style={{ minWidth: 0, display: "block" }}>
                  <span className="ic-keep-n" style={{ display: "block" }}>
                    {c.label}
                  </span>
                  {c.record_gone ? (
                    <span className="ic-keep-m" style={{ display: "block" }}>
                      This household was archived or merged since; pick another.
                    </span>
                  ) : (
                    <span className="ic-btns">
                      <button
                        type="button"
                        className="ic-btn ic-btn--primary"
                        disabled={disposition.busy || stub.stubGone || !onRequestHouseholdFieldMerge}
                        onClick={() =>
                          onRequestHouseholdFieldMerge?.([stub.stub, c], { defaultSurvivorId: c.id })
                        }
                      >
                        <Check {...ICON} aria-hidden />
                        {fix.label}
                      </button>
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : fix.picker === "link-fee-name" ? (
          <FeeNameLink issue={issue} onResolved={onResolved} />
        ) : (
          <div className="ic-btns">
            <button
              type="button"
              className="ic-btn ic-btn--primary"
              data-action="primary"
              disabled={disposition.busy || !open}
              onClick={() => open?.()}
            >
              <Check {...ICON} aria-hidden />
              {fix.label}
            </button>
          </div>
        )}
        <p className="ic-note">{fix.consequence}</p>
        {fix.picker === "stub-family" && stub?.stubGone && (
          <p className="ic-note">The placeholder is already gone, so there is nothing left to merge.</p>
        )}
        {disposition.err && <p className="ic-err">{disposition.err}</p>}
      </div>
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

/** "Which household is this billed name?" on the card: a household
 *  search, saved through Fee Review's own "Link to household" (which
 *  remembers the spelling so next month matches by itself). */
function FeeNameLink({ issue, onResolved }: { issue: CleanupIssueSummary; onResolved: () => void }) {
  const toast = useToast();
  const params = issue.recommendation?.params ?? {};
  const alias = typeof params.client_name === "string" ? params.client_name : "";
  const custodian = params.custodian === "betterment" || params.custodian === "altruist_schwab" ? params.custodian : null;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{ id: string; label: string; sublabel?: string | null }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      void api
        .searchRecords(q, { types: ["household"] })
        .then((res) => {
          if (live) setResults(res.results.map((r) => ({ id: r.id, label: r.label, sublabel: r.sub_label ?? null })));
        })
        .catch(() => {
          if (live) setResults([]);
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query]);

  async function link(householdId: string) {
    if (!custodian || !alias || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.linkCustodianName({ custodian, alias, household_id: householdId });
      try {
        await api.markCleanupIssueResolved(issue.id);
      } catch {
        /* the next fee review clears it */
      }
      toast.success(`Linked "${alias}" to ${res.household_label}. Next month's review matches it by itself.`);
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't link. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!custodian || !alias) {
    return <p className="ic-note">This item doesn't name the billed name; link it from Fee Review.</p>;
  }
  return (
    <div>
      <label className="ic-field-k" htmlFor={`ic-fee-${issue.id}`}>
        Which household is "{alias}"?
      </label>
      <input
        id={`ic-fee-${issue.id}`}
        className="ic-input"
        type="search"
        value={query}
        disabled={busy}
        placeholder="Search households"
        onChange={(e) => setQuery(e.target.value)}
      />
      {results.length > 0 && (
        <ul className="ic-keep ic-keep--plain">
          {results.slice(0, 8).map((r) => (
            <li key={r.id}>
              <span style={{ minWidth: 0, display: "block" }}>
                <span className="ic-keep-n" style={{ display: "block" }}>
                  {r.label}
                </span>
                {r.sublabel && (
                  <span className="ic-keep-m" style={{ display: "block" }}>
                    {r.sublabel}
                  </span>
                )}
                <span className="ic-btns">
                  <button type="button" className="ic-btn ic-btn--primary" disabled={busy} onClick={() => void link(r.id)}>
                    <Check {...ICON} aria-hidden />
                    Link to this household
                  </button>
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {err && <p className="ic-err">{err}</p>}
    </div>
  );
}

// ─── A win nothing was created from ─────────────────────────────────
//
// Something marked the opportunity Won outside the OS, so there is no
// Processing record. The two answers open the opportunity page's own Mark
// Won / Mark Lost dialog; when the person confirms, the stray win is
// reopened (server-guarded: never a win with a record behind it) and the
// ordinary win or loss runs. Nothing changes until they confirm.
function RewinCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedRewin }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const [open, setOpen] = useState<{ id: string; mode: "won" | "lost" } | null>(null);
  const [done, setDone] = useState<Record<string, "won" | "lost">>({});
  const row = open ? fix.rows.find((r) => r.id === open.id) ?? null : null;

  async function finish(id: string, outcome: "won" | "lost", undo: () => Promise<unknown>, said: string) {
    setOpen(null);
    const next = { ...done, [id]: outcome };
    setDone(next);
    toast.success(said, {
      action: {
        label: "Undo",
        onClick: () => {
          void undo()
            .then(() => toast.success("Undone. The opportunity is open again."))
            .catch(() => toast.error("Couldn't undo. Open the opportunity to change it."));
        },
      },
    });
    if (fix.rows.every((r) => next[r.id])) {
      try {
        await api.markCleanupIssueResolved(issue.id);
      } catch {
        /* the next sweep clears it: every win now has a file or is closed */
      }
      onResolved();
    }
  }

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">What's wrong</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it happened.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix · run it properly <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <ul className="ic-keep ic-keep--plain">
          {fix.rows.map((r) => (
            <li key={r.id}>
              <span style={{ minWidth: 0, display: "block" }}>
                <span className="ic-keep-n" style={{ display: "block" }}>
                  {r.label}
                </span>
                {done[r.id] ? (
                  <span className="ic-keep-m" style={{ display: "block" }}>
                    {done[r.id] === "won" ? "Won properly. The Processing record is open." : "Marked lost."}
                  </span>
                ) : (
                  <span className="ic-btns">
                    <button
                      type="button"
                      className="ic-btn ic-btn--primary"
                      disabled={disposition.busy}
                      onClick={() => setOpen({ id: r.id, mode: "won" })}
                    >
                      <Check {...ICON} aria-hidden />
                      Run the win properly
                    </button>
                    <button
                      type="button"
                      className="ic-btn ic-btn--ghost"
                      disabled={disposition.busy}
                      onClick={() => setOpen({ id: r.id, mode: "lost" })}
                    >
                      It didn't really close
                    </button>
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        <p className="ic-note">
          Asks the same questions as Mark Won or Mark Lost on the opportunity, and changes nothing
          until you confirm. Undo for 60 seconds leaves the opportunity open.
        </p>
        {disposition.err && <p className="ic-err">{disposition.err}</p>}
      </div>

      <OpportunityOutcomeModal
        mode={open && row ? open.mode : null}
        target={
          row
            ? {
                id: row.id,
                household_display_name: fix.householdName,
                company_slug: fix.companySlug,
                opportunity_type: row.opportunityType,
              }
            : null
        }
        beforeConfirm={async () => {
          if (open) await api.reopenStrayWin(issue.id, open.id);
        }}
        onClose={() => setOpen(null)}
        onWon={(res) =>
          void finish(
            res.opportunity_id,
            "won",
            () => api.undoOpportunityWon(res.opportunity_id, { reason_text: "Undo from Data Needed" }),
            "Won properly. The Processing record is open.",
          )
        }
        onLost={(res) =>
          void finish(
            res.opportunity_id,
            "lost",
            () => api.reopenJustLost(issue.id, res.opportunity_id),
            "Marked lost.",
          )
        }
      />

      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />

      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

function MissingDocumentsCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedMissingDocuments }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const [state, setState] = useState<MissingDocumentsState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notNeededFor, setNotNeededFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .getCleanupIssueMissingDocuments(issue.id)
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : "Couldn't load the documents.");
      });
    return () => {
      cancelled = true;
    };
  }, [issue.id]);

  // Nothing left missing: close the item (the server re-checks first).
  async function closeIfDone(next: MissingDocumentsState) {
    if (next.missing.length > 0) return;
    try {
      await api.decideCleanupIssue({ issue_id: issue.id, verb: "verify", overrode_recommendation: false });
      toast.success("Every required document is on file.");
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't close this item. Try again.");
    }
  }

  async function upload(definitionId: string, file: File) {
    setBusy(definitionId);
    setErr(null);
    try {
      await apiV11.uploadDocumentSharePoint(file, {
        entity_type: "service_record",
        entity_id: fix.record.id,
        document_type: definitionId,
      });
      toast.success(`Uploaded ${file.name}.`);
      const next = await api.getCleanupIssueMissingDocuments(issue.id);
      setState(next);
      await closeIfDone(next);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That upload didn't go through. Try again.");
    } finally {
      setBusy(null);
    }
  }

  async function markNotNeeded(definitionId: string) {
    const why = reason.trim();
    if (!why) return;
    setBusy(definitionId);
    setErr(null);
    try {
      const res = await api.markCleanupDocumentNotNeeded(issue.id, { definition_id: definitionId, reason: why });
      setState(res);
      setNotNeededFor(null);
      setReason("");
      if (res.resolved) {
        toast.success("Recorded: not needed. Every required document is now accounted for.", {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .withdrawCleanupDocumentNotNeeded(issue.id, res.waiver_id)
                .then(() => toast.success("Undone. Back on your list."))
                .catch(() => toast.error("Couldn't undo. Withdraw it from the record's documents."));
            },
          },
        });
        onResolved();
      } else {
        toast.success("Recorded: not needed on this record.");
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(null);
    }
  }

  async function withdraw(waiverId: string) {
    setBusy(waiverId);
    setErr(null);
    try {
      setState(await api.withdrawCleanupDocumentNotNeeded(issue.id, waiverId));
      toast.success("Undone: that document is required again.");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(null);
    }
  }

  const missing = state?.missing ?? [];
  const waived = state?.waived ?? [];
  return (
    <article className="ic-card">
      <div className="ic-eyebrow">{fix.title}</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix · upload each one, or say why it isn't needed <Pill tone="info">Needs you</Pill>
        </div>
        <p className="ic-meta">
          On{" "}
          <button type="button" className="ic-person-n" onClick={() => onOpenRecord(fix.record)}>
            {fix.record.label}
          </button>
        </p>
        {state === null && !err && <p className="ic-res-empty">Loading the documents…</p>}
        {state !== null && missing.length === 0 && (
          <p className="ic-note ic-note--ok">Every required document is on file or marked not needed.</p>
        )}
        {missing.length > 0 && (
          <ul className="ic-docs">
            {missing.map((d) => (
              <li key={d.definition_id} className="ic-doc">
                <div className="ic-doc-n">
                  {d.code ? `${d.code} ` : ""}
                  {d.name}
                  {d.when_required && <span className="ic-doc-w">{d.when_required}</span>}
                </div>
                <div className="ic-doc-a">
                  <label className={`ic-btn ic-btn--ghost ic-btn--sm${busy ? " is-disabled" : ""}`}>
                    <input
                      type="file"
                      hidden
                      disabled={busy !== null}
                      aria-label={`Upload ${d.name}`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (file) void upload(d.definition_id, file);
                      }}
                    />
                    {busy === d.definition_id ? "Working…" : "Upload"}
                  </label>
                  <button
                    type="button"
                    className="ic-btn ic-btn--ghost ic-btn--sm"
                    disabled={busy !== null}
                    onClick={() => {
                      setNotNeededFor(notNeededFor === d.definition_id ? null : d.definition_id);
                      setReason("");
                    }}
                  >
                    Not needed…
                  </button>
                </div>
                {notNeededFor === d.definition_id && (
                  <div className="ic-doc-why">
                    <label className="ic-field-k" htmlFor={`ic-why-${d.definition_id}`}>
                      Why isn't it needed on this record?
                    </label>
                    <input
                      id={`ic-why-${d.definition_id}`}
                      type="text"
                      className="ic-input"
                      maxLength={500}
                      value={reason}
                      disabled={busy !== null}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <button
                      type="button"
                      className="ic-btn ic-btn--primary ic-btn--auto"
                      disabled={busy !== null || !reason.trim()}
                      onClick={() => void markNotNeeded(d.definition_id)}
                    >
                      Mark not needed
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {waived.length > 0 && (
          <ul className="ic-docs ic-docs--waived">
            {waived.map((w) => (
              <li key={w.waiver_id} className="ic-doc">
                <div className="ic-doc-n">
                  Not needed: {w.name}
                  <span className="ic-doc-w">
                    “{w.reason}”{w.by ? ` · ${w.by}` : ""}
                  </span>
                </div>
                <div className="ic-doc-a">
                  <button
                    type="button"
                    className="ic-btn ic-btn--ghost ic-btn--sm"
                    disabled={busy !== null}
                    onClick={() => void withdraw(w.waiver_id)}
                  >
                    Undo
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="ic-note">
          Each upload is filed on this record under that document's name. "Not needed" is recorded at once with
          your reason and who said it, and can be undone. The item closes when nothing is left missing.
        </p>
        {err && <p className="ic-err">{err}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy !== null}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Retention (keep, hold, or destroy a document) ──────────────────
//
// 2026-10-02. The side panel's keep / hold / destroy panel, mounted on
// the card unchanged: admin-only on the server, destroy behind its typed
// DESTROY PERMANENTLY. The item drops off at the next sweep once the
// document is held, extended or destroyed (the panel's own endpoints do
// not go through /decide).

function RetentionCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedRetention }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  return (
    <article className="ic-card">
      <div className="ic-eyebrow">{fix.title}</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <RetentionDispositionPanel
          issue={issue}
          onResolved={(message) => {
            toast.success(message);
            onResolved();
          }}
        />
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Find the family (a placeholder household nobody has placed) ────
//
// 2026-10-02. An import made a household out of a borrower's name or an
// account title and could not tell whose it is. The answer is a search:
// "Find the family and merge…" opens the existing search-and-merge
// screen (pick the family, review every field, merge; reversible). The
// side panel had this button; the card had nothing.

function FindHouseholdCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
  onRequestHouseholdSearchMerge,
}: IssueCardProps & { fix: ResolvedFindHousehold }) {
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const source: RecordRef = { kind: "household", id: fix.household.id, label: fix.household.label };
  return (
    <article className="ic-card">
      <div className="ic-eyebrow">{fix.title}</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix · find the family <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <p className="ic-meta">
          Household:{" "}
          <button type="button" className="ic-person-n" onClick={() => onOpenRecord(source)}>
            {fix.household.label}
          </button>
        </p>
        {onRequestHouseholdSearchMerge ? (
          <div className="ic-btns">
            <button
              type="button"
              className="ic-btn ic-btn--primary"
              data-action="primary"
              disabled={disposition.busy}
              onClick={() => onRequestHouseholdSearchMerge(source)}
            >
              <Search {...ICON} aria-hidden />
              Find the family and merge…
            </button>
          </div>
        ) : (
          <p className="ic-res-empty">Use More options… to search for the family.</p>
        )}
        <p className="ic-note">
          Search for the household this belongs to, then review every field side by side before anything
          moves. Everything on it moves to the household you pick, nothing is deleted, and it can be undone. If
          you can't tell yet, use Later or Assign below.
        </p>
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Choose an answer (two to four real answers) ────────────────────
//
// 2026-10-02. "Is this a business, not a person?" and "two people in one
// contact" each have a few real answers, and each answer is a different
// change. Every answer is a button with one line under it saying what it
// does; the recommended one leads. The card sends exactly what the side
// panel's button for that answer sends (verb, params, audit reason), so
// the decide route's own branch validates it, writes it and hands back
// its undo.

function ChooseAnswerCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
  onRequestHouseholdSearchMerge,
}: IssueCardProps & { fix: ResolvedChooseAnswer }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const hasRecommended = fix.answers.some((a) => a.recommended);
  const household = issue.affectedRecords.find((r) => r.kind === "household") ?? null;
  // "Find it and merge" opens the host's search-and-merge screen; a host
  // without one (or an issue without its household) drops that answer
  // rather than show a button that does nothing.
  const answers = fix.answers.filter(
    (a) => a.verb !== "find-household" || (onRequestHouseholdSearchMerge != null && household != null),
  );

  // An answer that needs a reason opens a box first; the button under it
  // records the answer with that reason.
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [note, setNote] = useState("");

  async function choose(answer: CleanupChooseAnswer, reason?: string) {
    if (busy) return;
    if (answer.needsNote && !reason) {
      setNoteFor(answer.verb);
      return;
    }
    if (answer.verb === "find-household") {
      if (onRequestHouseholdSearchMerge && household) {
        onRequestHouseholdSearchMerge({ kind: "household", id: household.id, label: household.label });
      }
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: answer.verb,
        ...(answer.params ? { params: { ...answer.params } } : {}),
        ...(reason ? { reason_text: reason } : answer.reasonText ? { reason_text: answer.reasonText } : {}),
        overrode_recommendation: hasRecommended && !answer.recommended,
      });
      if (res?.undo?.available) {
        const undo = res.undo;
        toast.success(answer.done, {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                .then(() => toast.success("Undone. Back on your list."))
                .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
            },
          },
        });
      } else {
        toast.success(answer.done);
      }
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">{fix.title}</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          Your answer <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <div className="ic-answers">
          {answers.map((a) => (
            <div key={a.verb} className="ic-answer">
              <button
                type="button"
                className={`ic-btn ${a.recommended ? "ic-btn--primary" : "ic-btn--ghost"}`}
                {...(a.recommended ? { "data-action": "primary" } : {})}
                disabled={busy}
                onClick={() => void choose(a)}
              >
                {a.recommended && <Check {...ICON} aria-hidden />}
                {a.label}
              </button>
              <p className="ic-answer-c">{a.consequence}</p>
              {noteFor === a.verb && (
                <>
                  <label className="ic-field-k" htmlFor={`ic-ans-note-${issue.id}`}>
                    Why
                  </label>
                  <textarea
                    id={`ic-ans-note-${issue.id}`}
                    className="ic-input"
                    rows={2}
                    maxLength={500}
                    value={note}
                    disabled={busy}
                    placeholder="A short reason, so the next person knows"
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <button
                    type="button"
                    className="ic-btn ic-btn--ghost"
                    disabled={busy || note.trim() === ""}
                    onClick={() => void choose(a, note.trim())}
                  >
                    Record: {a.label}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        {err && <p className="ic-err">{err}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Fill one value (step 3: the rail's one input, on the card) ─────
//
// 2026-10-02. Eight items whose decide-rail answer was one box (a date, a
// price, a name, a program, a member). The card sends exactly what the
// rail sends: `verify` with the issue's recommendation params plus the
// declared param, or a declared "we don't know" escape. The decide route's
// branch for each detector validates, writes and returns the undo.

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function memberName(c: HouseholdContact): string {
  const first = c.preferred_name?.trim() || c.legal_first_name?.trim() || "";
  return [first, c.legal_last_name?.trim() ?? ""].filter(Boolean).join(" ") || "Unnamed member";
}

function fillDisplay(
  fix: ResolvedFillOne,
  raw: string,
  members: HouseholdContact[] | null,
  advisors: AssignableUser[] | null,
): string {
  const t = fix.input.type;
  if (t === "date") {
    const d = new Date(`${raw}T00:00:00Z`);
    return Number.isNaN(d.getTime())
      ? raw
      : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  }
  if (t === "money") {
    const n = parseSalePriceInput(raw);
    return n === null ? raw : `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  }
  if (t === "select") return fix.input.options?.find((o) => o.value === raw)?.label ?? raw;
  if (t === "member") {
    const m = members?.find((c) => c.id === raw);
    return m ? memberName(m) : raw;
  }
  if (t === "advisor") {
    const a = advisors?.find((u) => u.id === raw);
    return a ? (a.name ?? a.email) : raw;
  }
  return `"${raw.trim()}"`;
}

/** The value to send, or null when the box does not hold a valid answer. */
function fillValue(fix: ResolvedFillOne, raw: string): string | number | null {
  const t = fix.input.type;
  if (t === "date") {
    if (!ISO_DAY_RE.test(raw)) return null;
    const d = new Date(`${raw}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === raw ? raw : null;
  }
  if (t === "money") return parseSalePriceInput(raw);
  if (t === "text") {
    const v = raw.trim();
    return v.length >= (fix.input.minLength ?? 1) ? v : null;
  }
  return raw ? raw : null;
}

function lowerFirst(s: string): string {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

function FillOneCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedFillOne }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const input = fix.input;
  const [raw, setRaw] = useState("");
  const [note, setNote] = useState("");
  const [ticked, setTicked] = useState(false);
  const [members, setMembers] = useState<HouseholdContact[] | null>(null);
  const [advisors, setAdvisors] = useState<AssignableUser[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (input.type !== "advisor" || !input.company) return;
    let cancelled = false;
    api
      .listCompanyAdvisors(input.company)
      .then((r) => {
        if (!cancelled) setAdvisors(r.users);
      })
      .catch(() => {
        if (!cancelled) {
          setAdvisors([]);
          setErr("Couldn't load the advisors. Use More options… to answer from the full panel.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [input.type, input.company]);

  useEffect(() => {
    if (input.type !== "member" || !fix.householdId) return;
    let cancelled = false;
    api
      .getHousehold(fix.householdId)
      .then((d) => {
        if (!cancelled) setMembers(d.contacts);
      })
      .catch(() => {
        if (!cancelled) {
          setMembers([]);
          setErr("Couldn't load the household's people. Use More options… to answer from the full panel.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [input.type, fix.householdId]);

  const value = fillValue(fix, raw);
  const blank = raw.trim() === "";
  const ready = value !== null || (blank && input.optional === true);
  const label =
    value !== null
      ? fix.applyLabel.replace("{value}", fillDisplay(fix, raw, members, advisors))
      : blank && input.optional && fix.blankApplyLabel
        ? fix.blankApplyLabel
        : fix.applyLabel.replace("{value}", "…");

  async function send(verb: string, params: Record<string, unknown>, done: string) {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({ issue_id: issue.id, verb, params });
      if (res?.undo?.available) {
        const undo = res.undo;
        toast.success(done, {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                .then(() => toast.success("Back on your list."))
                .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
            },
          },
        });
      } else {
        toast.success(done);
      }
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function apply() {
    if (!ready) return;
    // The rail spreads the detector's own params (the ids the server pins
    // the write to) under the answer; the card does the same.
    const params: Record<string, unknown> = { ...(issue.recommendation?.params ?? {}) };
    if (value !== null) params[input.paramKey] = value;
    if (input.otherNote && raw === input.otherNote.whenValue && note.trim()) {
      params[input.otherNote.paramKey] = note.trim();
    }
    if (input.checkbox && ticked) params[input.checkbox.paramKey] = true;
    void send(fix.verb, params, `Saved: ${label}.`);
  }

  const id = `ic-fill-${issue.id}`;
  return (
    <article className="ic-card">
      <div className="ic-eyebrow">What's missing</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix ic-fill">
        <div className="ic-eyebrow">
          The fix · type it in <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <label className="ic-field-k" htmlFor={id}>
          {input.label}
        </label>
        {input.hint && <p className="ic-meta">{input.hint}</p>}
        <p className={input.current ? "ic-fill-cur ic-note--ok" : "ic-fill-cur ic-fill-cur--none"}>
          {input.current ? `On file: ${input.current}` : "Not on file"}
        </p>
        {input.type === "date" && (
          <input
            id={id}
            type="date"
            className="ic-input"
            value={raw}
            disabled={busy}
            onChange={(e) => setRaw(e.target.value)}
          />
        )}
        {(input.type === "text" || input.type === "money") && (
          <input
            id={id}
            type="text"
            inputMode={input.type === "money" ? "decimal" : undefined}
            className="ic-input"
            value={raw}
            disabled={busy}
            placeholder={input.placeholder ?? (input.type === "money" ? "$" : undefined)}
            onChange={(e) => setRaw(e.target.value)}
          />
        )}
        {(input.type === "select" || input.type === "member" || input.type === "advisor") && (
          <select
            id={id}
            className="ic-input"
            value={raw}
            disabled={
              busy ||
              (input.type === "member" && members === null) ||
              (input.type === "advisor" && advisors === null)
            }
            onChange={(e) => setRaw(e.target.value)}
          >
            <option value="">
              {input.type === "member" && members === null
                ? "Loading the household's people…"
                : input.type === "advisor" && advisors === null
                  ? "Loading the advisors…"
                  : (input.placeholder ?? "Choose…")}
            </option>
            {input.type === "select" &&
              input.options?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            {input.type === "member" &&
              members?.map((m) => (
                <option key={m.id} value={m.id}>
                  {memberName(m)}
                  {m.household_role === "primary" ? " (marked primary now)" : ""}
                </option>
              ))}
            {input.type === "advisor" &&
              advisors?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ?? a.email}
                </option>
              ))}
          </select>
        )}
        {input.type === "advisor" && advisors !== null && advisors.length === 0 && (
          <p className="ic-err">
            Nobody is set up as an advisor for this company yet. An admin adds the company to an advisor's access on
            the Team page.
          </p>
        )}
        {input.otherNote && raw === input.otherNote.whenValue && (
          <>
            <label className="ic-field-k" htmlFor={`${id}-note`}>
              {input.otherNote.label}
            </label>
            <input
              id={`${id}-note`}
              type="text"
              className="ic-input"
              value={note}
              maxLength={200}
              disabled={busy}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        )}
        {input.checkbox && (
          <label className="ic-fill-check">
            <input type="checkbox" checked={ticked} disabled={busy} onChange={(e) => setTicked(e.target.checked)} />
            <span>{input.checkbox.label}</span>
          </label>
        )}
        <div className="ic-btns">
          <button
            type="button"
            className="ic-btn ic-btn--primary"
            data-action="primary"
            disabled={!ready || busy}
            onClick={apply}
          >
            <Check {...ICON} aria-hidden />
            {label}
          </button>
          {fix.escapes.map((e) => (
            <button
              key={e.label}
              type="button"
              className="ic-btn ic-btn--ghost"
              disabled={busy}
              onClick={() => void send(e.verb, { ...(e.params ?? {}) }, `Recorded: ${e.label}.`)}
            >
              {e.label}
            </button>
          ))}
        </div>
        <p className="ic-note">
          Saves straight to the record, the same as the full panel under More options. You can undo for 60
          seconds.
          {fix.escapes.map((e) => ` "${e.label}" ${lowerFirst(e.consequence)}`).join("")}
        </p>
        {err && <p className="ic-err">{err}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Add the policy (a carrier row that never landed) ───────────────
//
// import-quarantine (2026-10-02). The importer could not place a carrier
// row, so the policy is missing from the OS: no renewal plan, no premium in
// any total. The card does the work in place: find the household, then the
// household page's own Add Policy form opens here, pre-filled from the
// carrier row. Saving creates the policy through the same endpoint (and
// the same Insurance write check) as the household page, then tells the
// decide route, which checks the policy really exists before closing the
// item. "Not a real policy" is a dismiss with a required reason.

type AddPolicyStage = "ask" | "search" | "form" | "not-real";

function AddPolicyCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedAddPolicy }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const [stage, setStage] = useState<AddPolicyStage>("ask");
  const [household, setHousehold] = useState<{ id: string; label: string } | null>(null);
  const [members, setMembers] = useState<HouseholdContact[] | null>(null);
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const p = fix.prefill;

  async function pickHousehold(h: { id: string; label: string }) {
    setHousehold(h);
    setMembers(null);
    setErr(null);
    setStage("form");
    try {
      const detail = await api.getHousehold(h.id);
      setMembers(detail.contacts);
      setPrimaryId(detail.household.primary_contact_id ?? null);
    } catch {
      setMembers([]);
      setErr("Couldn't load the household's people. Pick the household again, or add the policy from its page.");
    }
  }

  async function confirmAdded(serviceRecordId: string) {
    try {
      await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: "verify",
        params: { service_record_id: serviceRecordId },
      });
      toast.success(`Policy added to ${household?.label ?? "the household"}.`);
    } catch (e) {
      // The policy exists either way; only the item's closing failed, and
      // the next sweep closes it because the policy has landed.
      toast.error(
        e instanceof ApiError
          ? `Policy added, but this item didn't close: ${e.message}`
          : "Policy added, but this item didn't close. It clears on the next check.",
      );
    }
    onResolved();
  }

  async function dismissNotReal() {
    const why = reason.trim();
    if (!why || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: "dismiss",
        reason_text: `Not a real policy: ${why}`.slice(0, 500),
      });
      if (res?.undo?.available) {
        const undo = res.undo;
        toast.success("Dismissed: not a real policy.", {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                .then(() => toast.success("Back on your list."))
                .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
            },
          },
        });
      }
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (stage === "search") {
    return (
      <PolicyHouseholdSearch
        issueId={issue.id}
        what={[p.carrier, p.policyNumber ? `policy ${p.policyNumber}` : null].filter(Boolean).join(" ") || "this policy"}
        initialQuery={fix.searchHint ?? ""}
        onPick={(h) => void pickHousehold(h)}
        onBack={() => setStage("ask")}
      />
    );
  }

  if (stage === "form" && household) {
    return (
      <article className="ic-card">
        <div className="ic-eyebrow">Add the policy</div>
        <h2 className="ic-q">Add it to {household.label}</h2>
        <p className="ic-how">
          Filled in from the carrier row. Check the type, dates and premium at the carrier, pick who it is for,
          then save. It is the same form as the household page.
        </p>
        {members === null ? (
          <p className="ic-res-empty">Loading the household's people…</p>
        ) : (
          <AddInsurancePolicyInlineForm
            householdId={household.id}
            householdContacts={members}
            primaryContactId={primaryId}
            startOpen
            initial={{
              carrier: p.carrier,
              policyNumber: p.policyNumber,
              notes: p.insuredName ? `Insured on the carrier row: ${p.insuredName}` : null,
            }}
            onCancel={() => setStage("search")}
            onCreated={(created) => confirmAdded(created.service_record_id)}
            onSaved={() => undefined}
          />
        )}
        {err && <p className="ic-err">{err}</p>}
        <button type="button" className="ic-lnk ic-lnk--quiet" onClick={() => setStage("search")}>
          Different household
        </button>
      </article>
    );
  }

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">What's missing</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <ul className="ic-kv">
          <li>
            <span>Carrier</span>
            <span>{p.carrier ?? "Not on the row"}</span>
          </li>
          <li>
            <span>Policy number</span>
            <span>{p.policyNumber ?? "Not on the row"}</span>
          </li>
          <li>
            <span>Insured</span>
            <span>{p.insuredName ?? "Not on the row"}</span>
          </li>
        </ul>
        {stage === "not-real" ? (
          <>
            <label className="ic-field-k" htmlFor={`ic-notreal-${issue.id}`}>
              Why it isn't a real policy
            </label>
            <textarea
              id={`ic-notreal-${issue.id}`}
              className="ic-input"
              rows={2}
              maxLength={400}
              value={reason}
              disabled={busy}
              placeholder="A header line, a duplicate row, a cancelled policy…"
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="ic-btns">
              <button
                type="button"
                className="ic-btn ic-btn--primary"
                data-action="primary"
                disabled={!reason.trim() || busy}
                onClick={() => void dismissNotReal()}
              >
                <X {...ICON} aria-hidden />
                Dismiss it
              </button>
              <button type="button" className="ic-lnk ic-lnk--quiet" disabled={busy} onClick={() => setStage("ask")}>
                Back
              </button>
            </div>
            <p className="ic-note">
              Nothing is created. The item closes with your reason and does not come back. You can undo for 60
              seconds.
            </p>
          </>
        ) : (
          <>
            <div className="ic-btns">
              <button
                type="button"
                className="ic-btn ic-btn--primary"
                data-action="primary"
                onClick={() => setStage("search")}
              >
                <Search {...ICON} aria-hidden />
                It's real: find the household
              </button>
              <button type="button" className="ic-btn ic-btn--ghost" onClick={() => setStage("not-real")}>
                Not a real policy
              </button>
            </div>
            <p className="ic-note">
              Finding the household opens the Add Policy form here, already filled in from the carrier row.
              Nothing is saved until you press Save policy.
            </p>
          </>
        )}
        {err && <p className="ic-err">{err}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

/** Household search for the add-policy card. Same search as the move
 *  card's picker, without a suggested or current household: the importer
 *  could not say whose policy this is, so nothing is offered first. */
function PolicyHouseholdSearch({
  issueId,
  what,
  initialQuery,
  onPick,
  onBack,
}: {
  issueId: string;
  what: string;
  initialQuery: string;
  onPick: (h: { id: string; label: string }) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchErr(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({ q: q.trim(), limit: 20 });
        if (cancelled) return;
        setResults(res.households);
        setSearchErr(null);
      } catch (e) {
        if (cancelled) return;
        setSearchErr(e instanceof ApiError ? e.message : "Search failed");
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">Pick the household</div>
      <h2 className="ic-q">Whose policy is {what}?</h2>
      <label className="ic-srch" htmlFor={`ic-search-${issueId}`}>
        <Search {...ICON} aria-hidden />
        <input
          id={`ic-search-${issueId}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search households by name…"
          autoFocus
        />
      </label>
      <ol className="ic-res">
        {results.map((h) => (
          <li key={h.id}>
            <div>
              <div className="ic-res-n">{h.display_name}</div>
              <div className="ic-res-m">
                {[
                  h.members_label,
                  `${h.contact_count} ${h.contact_count === 1 ? "person" : "people"}`,
                  h.primary_city && h.primary_state ? `${h.primary_city}, ${h.primary_state}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button
              type="button"
              className="ic-btn ic-btn--ghost ic-btn--sm"
              onClick={() => onPick({ id: h.id, label: h.display_name })}
            >
              Add it here
            </button>
          </li>
        ))}
      </ol>
      {searching && <p className="ic-res-empty">Searching…</p>}
      {searchErr && <p className="ic-err">{searchErr}</p>}
      {!searching && q.trim().length >= 2 && results.length === 0 && !searchErr && (
        <p className="ic-res-empty">
          No household matches "{q.trim()}". Try another spelling, or create the household first and come back.
        </p>
      )}
      {q.trim().length < 2 && <p className="ic-res-empty">Type at least two letters of the family's name.</p>}
      <button type="button" className="ic-lnk ic-lnk--quiet" onClick={onBack}>
        Back
      </button>
    </article>
  );
}

// ─── Keep one, retire the rest ──────────────────────────────────────

function KeepOneCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedKeepOne }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const [keeperId, setKeeperId] = useState<string | null>(fix.keeperId);
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // "They're all real" (fix.keepAll): asks why before it closes the item.
  const [keepAllOpen, setKeepAllOpen] = useState(false);
  const [keepAllNote, setKeepAllNote] = useState("");

  const busy = working || disposition.busy;
  const pill = confidencePill(fix.confidence);
  const keeper = fix.candidates.find((c) => c.id === keeperId) ?? null;
  const retiring = fix.candidates.filter((c) => c.id !== keeperId);
  const noun = movableNoun(fix.candidates[0]!.kind);

  // Merge mode (duplicate policy rows): the others fold into the one kept
  // through the decide route's merge, which pins every id to this item
  // and returns its own undo. A confirm-tier item needs a reason code.
  const [reasonCode, setReasonCode] = useState("");
  const needsReason = fix.retire === "merge" && issue.tier === "confirm";
  async function mergeIn() {
    if (!keeper || working || (needsReason && !reasonCode)) return;
    setWorking(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: "merge",
        params: { survivor_id: keeper.id, merged_ids: retiring.map((r) => r.id) },
        ...(reasonCode ? { reason_code: reasonCode } : {}),
        ...(issue.recommendation?.params?.["survivor_id"] !== keeper.id ? { overrode_recommendation: true } : {}),
      });
      const undo = res?.undo;
      toast.success(
        `Kept "${keeper.label}". Merged ${retiring.length} in.`,
        undo?.available
          ? {
              action: {
                label: "Undo",
                onClick: () => {
                  void api
                    .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                    .then(() => toast.success("Put back."))
                    .catch(() => toast.error("Couldn't undo. The record's History can roll it back."));
                },
              },
            }
          : undefined,
      );
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through — try again.");
    } finally {
      setWorking(false);
    }
  }

  async function keepOne() {
    if (fix.retire === "merge") return mergeIn();
    if (!keeper || working) return;
    setWorking(true);
    setErr(null);
    // Retire each extra through the archive action that handles every
    // record kind (POST /api/archive/execute). One call per record — it
    // takes a single root — so the card holds every bulk action id and
    // the Undo replays them in reverse.
    const undoIds: string[] = [];
    try {
      for (const record of retiring) {
        const res = await api.archiveExecute({
          kind: record.kind,
          id: record.id,
          reason: `Duplicate of "${keeper.label}" — retired from Cleanup`,
          reason_code: "duplicate_import_artifact",
        });
        undoIds.push(res.bulk_action_id);
      }
      toast.success(
        `Kept "${keeper.label}". Retired ${retiring.length} ${
          retiring.length === 1 ? noun : `${noun}s`
        }.`,
        {
          action: {
            label: "Undo",
            onClick: () => {
              void (async () => {
                try {
                  for (const id of [...undoIds].reverse()) await api.archiveUndo(id);
                  toast.success("Put back.");
                } catch {
                  toast.error("Undo failed — one of them may have changed.");
                }
              })();
            },
          },
        },
      );
      try {
        await api.markCleanupIssueResolved(issue.id);
      } catch {
        /* the ~6h sweep will resolve it; don't block the UI */
      }
      onResolved();
    } catch (e) {
      // Some may already be retired. Say so plainly rather than implying
      // nothing happened.
      const partial =
        undoIds.length > 0
          ? ` ${undoIds.length} of ${retiring.length} were already retired — reopen the card to finish.`
          : "";
      setErr(
        (e instanceof ApiError ? e.message : "That didn't go through — try again.") + partial,
      );
    } finally {
      setWorking(false);
    }
  }

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">What's wrong</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>

      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it happened.</b> {issue.howItHappened}
        </p>
      )}

      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix · keep one <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        {!fix.keeperId && (
          <div className="ic-check">
            <AlertTriangle {...ICON} aria-hidden />
            <div>
              <b>Nothing here says which one is real.</b> Open them from "Why we think so" before
              you choose.
            </div>
          </div>
        )}
        <ol className="ic-keep">
          {fix.candidates.map((c) => (
            <li key={c.id} className={c.id === keeperId ? "is-on" : undefined}>
              <label>
                <input
                  type="radio"
                  name={`ic-keep-${issue.id}`}
                  value={c.id}
                  checked={c.id === keeperId}
                  disabled={busy}
                  onChange={() => setKeeperId(c.id)}
                />
                <span style={{ minWidth: 0 }}>
                  <span className="ic-keep-n" style={{ display: "block" }}>
                    {c.label}
                  </span>
                  {c.members_label && (
                    <span className="ic-keep-m" style={{ display: "block" }}>
                      {c.members_label}
                    </span>
                  )}
                </span>
                {c.id === fix.keeperId ? (
                  <Pill tone="sure">Suggested</Pill>
                ) : (
                  <span />
                )}
              </label>
            </li>
          ))}
        </ol>
        {needsReason && (
          <>
            <label className="ic-field-k" htmlFor={`ic-keep-reason-${issue.id}`}>
              Why
            </label>
            <select
              id={`ic-keep-reason-${issue.id}`}
              className="ic-input"
              value={reasonCode}
              disabled={busy}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              <option value="">Choose a reason…</option>
              <option value="duplicate_import_artifact">A duplicate an import made</option>
              <option value="data_quality_cleanup">Data cleanup</option>
              <option value="compliance_correction">Compliance correction</option>
            </select>
          </>
        )}
        <button
          type="button"
          className="ic-btn ic-btn--primary"
          disabled={busy || !keeper || (needsReason && !reasonCode)}
          onClick={() => void keepOne()}
        >
          <Check {...ICON} aria-hidden />
          {working
            ? fix.retire === "merge"
              ? "Merging…"
              : "Retiring…"
            : !keeper
              ? "Pick the one to keep"
              : fix.retire === "merge"
                ? `Keep this one, merge ${retiring.length} in`
                : `Keep this one, retire ${retiring.length}`}
        </button>
        <p className="ic-note">
          {fix.retire === "merge"
            ? "The others' history folds into the one you keep, and they retire. Undoable for 60 seconds, and from the record's History."
            : `Retired ${retiring.length === 1 ? `${noun} stays` : `${noun}s stay`} in history and can be put back. Undoable for 60 seconds.`}
        </p>
        {fix.keepAll ? (
          <div className="ic-alt">
            {!keepAllOpen ? (
              <button
                type="button"
                className="ic-lnk ic-lnk--quiet"
                disabled={busy}
                onClick={() => setKeepAllOpen(true)}
              >
                {fix.keepAll.label}
              </button>
            ) : (
              <>
                <p className="ic-meta">{fix.keepAll.consequence}</p>
                <label className="ic-field-k" htmlFor={`ic-keepall-${issue.id}`}>
                  Why
                </label>
                <textarea
                  id={`ic-keepall-${issue.id}`}
                  className="ic-input"
                  rows={2}
                  maxLength={500}
                  value={keepAllNote}
                  disabled={busy}
                  placeholder="A short reason, so the next person knows"
                  onChange={(e) => setKeepAllNote(e.target.value)}
                />
                <button
                  type="button"
                  className="ic-btn ic-btn--ghost"
                  disabled={busy || keepAllNote.trim() === ""}
                  onClick={() => void disposition.decide("dismiss", keepAllNote.trim())}
                >
                  Keep them all
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="ic-alt">
            <button
              type="button"
              className="ic-lnk ic-lnk--quiet"
              disabled={busy}
              onClick={() => void disposition.decide("dismiss")}
            >
              They're not duplicates, leave them
            </button>
          </div>
        )}
        {(err || disposition.err) && <p className="ic-err">{err ?? disposition.err}</p>}
      </div>

      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />

      <CardFooter
        issueId={issue.id}
        busy={busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Type it in ─────────────────────────────────────────────────────
//
// The value is missing and only a person can supply it (Minimum Viable
// Typing category 6). The card mounts the SAME editor the record page
// mounts, against the live record, and saves through the same endpoint.
//
// It deliberately does not build a form from the declaration. The words
// on these fields are company-specific and already have one home
// (shared/deal-value/metric-label.ts, personRoleNoun) — a second form
// stating them would be the "one home per fact" rule broken in the UI
// layer. So the detector names an editor and this mounts it.
//
// Both editors write ordinary record fields, not structural changes, so
// there is no bulk-action handle and no Undo toast: the value is
// versioned and field-audited like any edit, and typing a different one
// is the undo. The card says that rather than implying a 60-second
// window it does not have.

/** Open enough to keep the card up; anything else and the issue is done. */
const CARD_OPEN_STATES: readonly string[] = [
  "new",
  "ready-for-review",
  "in-research",
  "reopened",
  "snoozed",
];

function TypeItInCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedTypeItIn }) {
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const isPeople = fix.editor === "record-people";
  return (
    <article className="ic-card">
      {/* The mockup's own eyebrow for this card (screen 6): the record is
          not wrong, it is incomplete, and the words should say which. */}
      <div className="ic-eyebrow">What's missing</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>

      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it happened.</b> {issue.howItHappened}
        </p>
      )}

      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix · {isPeople ? "say who it's for" : "type it in"}{" "}
          <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <TypeItInEditor fix={fix} issueId={issue.id} onResolved={onResolved} />
        <p className="ic-note">
          {isPeople
            ? "Saves onto the record itself, the same as editing it on the record page. Every change is logged with who made it."
            : "Saves onto the record itself, the same as typing it on the record page. Nothing is archived or moved, so there's no Undo — type a different number and the new one stands."}
        </p>
      </div>

      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />

      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

/** Loads the live record, then mounts whichever editor was named. */
function TypeItInEditor({
  fix,
  issueId,
  onResolved,
}: {
  fix: ResolvedTypeItIn;
  issueId: string;
  onResolved: () => void;
}) {
  const [detail, setDetail] = useState<TypeItInRecord | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const { kind, id } = fix.record;
  useEffect(() => {
    let live = true;
    setLoadErr(null);
    void (async () => {
      try {
        const loaded = await loadTypeItInRecord(kind, id);
        if (live) setDetail(loaded);
      } catch (e) {
        if (live) {
          setLoadErr(
            e instanceof ApiError ? e.message : "Couldn't load this record — try reopening it.",
          );
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [kind, id, reloadKey]);

  /** Did that answer finish the issue? The detectors re-check the record
   *  on every write, so the issue itself is the honest source — not our
   *  own guess at whether enough was filled in. */
  async function settleIssue(stillOpenNote: string) {
    try {
      const res = await api.getCleanupIssue(issueId);
      if (!CARD_OPEN_STATES.includes(res.issue.state)) {
        onResolved();
        return;
      }
    } catch {
      /* fall through — showing the card again is the safe answer */
    }
    setNote(stillOpenNote);
    setReloadKey((k) => k + 1);
  }

  async function guard(fn: () => Promise<void>) {
    setBusy(true);
    setSaveErr(null);
    setNote(null);
    try {
      await fn();
    } catch (e) {
      setSaveErr(e instanceof ApiError ? e.message : "That didn't save — try again.");
    } finally {
      setBusy(false);
    }
  }

  if (loadErr) return <p className="ic-err">{loadErr}</p>;
  if (!detail) return <p className="ic-note">Loading the record…</p>;

  return (
    <div className="ic-type">
      {fix.editor === "deal-numbers" ? (
        <NumbersEditor
          item={judgmentItemFor(detail, fix)}
          busy={busy}
          onSave={(patch) =>
            void guard(async () => {
              const res =
                detail.kind === "opportunity"
                  ? await api.patchOpportunityNumbers(detail.id, {
                      expected_close_date: patch.expected_close_date,
                      product_metric: patch.product_metric,
                      estimated_revenue_year_1: patch.estimated_revenue_year_1,
                    })
                  : await api.patchTransactionNumbers(detail.id, patch);
              await settleIssue(
                res.judgment_complete
                  ? "Saved."
                  : "Saved — fill the rest of the numbers to clear this.",
              );
            })
          }
          onMarkUnknown={() =>
            void guard(async () => {
              await (detail.kind === "opportunity"
                ? api.markOpportunityNumbersUnknown(detail.id)
                : api.markTransactionNumbersUnknown(detail.id));
              await settleIssue("Recorded as not knowable.");
            })
          }
          {...(detail.kind === "opportunity"
            ? {
                onMarkLost: (reason: string) =>
                  void guard(async () => {
                    await api.transitionOpportunityToLost(detail.id, {
                      outcome_reason: reason,
                    });
                    await settleIssue("Closed as lost.");
                  }),
              }
            : {})}
        />
      ) : (
        <RecordPeopleControl
          adapter={peopleAdapterFor(detail)}
          householdId={fix.householdId ?? detail.householdId}
          noun={detail.personNoun}
          people={detail.people}
          onSaved={() => void settleIssue("Saved — anyone else still missing?")}
          emptyStateText={`Nobody is on this record yet. Add the ${detail.personNoun.toLowerCase()} so the household reads whose record this is.`}
        />
      )}
      {note && <p className="ic-note ic-note--ok">{note}</p>}
      {saveErr && <p className="ic-err">{saveErr}</p>}
    </div>
  );
}

/** The live record, flattened to the handful of facts both editors read. */
interface TypeItInRecord {
  kind: TypeItInRecordKind;
  id: string;
  label: string;
  householdId: string;
  companySlug: string;
  people: ServiceRecordPerson[];
  personNoun: string;
  /** Edits write through to the linked service record when there is one. */
  linkedServiceRecordId: string | null;
  peopleSourceKind: "service_record" | "transaction";
  numbers: MyJudgmentItemNumbers;
  closedOn: string | null;
  /** Realty + linked property only: BrokerMint's three metrics, prefilled. */
  realtyCommission: {
    office_gross: number | null;
    agent_net: number | null;
    office_net: number | null;
  } | null;
}

async function loadTypeItInRecord(kind: TypeItInRecordKind, id: string): Promise<TypeItInRecord> {
  if (kind === "opportunity") {
    const { opportunity: o } = await api.getOpportunityDetail(id);
    return {
      kind,
      id: o.id,
      label: o.household_display_name,
      householdId: o.household_id,
      companySlug: o.company_slug,
      people: o.people,
      // An opportunity has no product yet, so it has no product-specific
      // role word — the record page says "Person" here too.
      personNoun: "Person",
      linkedServiceRecordId: null,
      peopleSourceKind: "transaction",
      numbers: {
        expected_close_date: o.expected_close_date,
        product_metric: o.product_metric,
        estimated_revenue_year_1: o.estimated_revenue_year_1,
      },
      closedOn: null,
      realtyCommission: null,
    };
  }
  const { transaction: t } = await api.getTransactionDetail(id);
  // The BrokerMint breakdown lives on the linked property record, so the
  // three-field form only appears once one is linked — the same rule the
  // Data Needed editor follows. One extra fetch, realty only.
  let realtyCommission: TypeItInRecord["realtyCommission"] = null;
  const realtyRecordId = t.realty_property?.realty_record_id ?? null;
  if (t.company_slug === "realty" && realtyRecordId) {
    try {
      const r = await api.getRealtyRecord(realtyRecordId);
      realtyCommission = {
        office_gross: r.office_gross,
        agent_net: r.agent_net,
        office_net: r.office_net,
      };
    } catch {
      /* no breakdown to prefill — the editor falls back to Office net alone */
    }
  }
  return {
    kind,
    id: t.id,
    label: t.household_display_name,
    householdId: t.household_id,
    companySlug: t.company_slug,
    people: t.people,
    personNoun: personRoleNoun(t.service_family ?? "", t.service_type),
    linkedServiceRecordId: t.linked_service_record_id ?? null,
    peopleSourceKind: t.people_source_kind,
    numbers: {
      expected_close_date: t.expected_close_date,
      product_metric: t.product_metric,
      estimated_revenue_year_1: t.estimated_revenue_year_1,
    },
    closedOn: t.actual_close_date,
    realtyCommission,
  };
}

/** Whose endpoints the people edits go to — the linked service record's
 *  when one is linked (single source of truth), else the record's own.
 *  Same precedence the record page uses. */
function peopleAdapterFor(detail: TypeItInRecord) {
  if (
    detail.kind === "transaction" &&
    detail.peopleSourceKind === "service_record" &&
    detail.linkedServiceRecordId
  ) {
    return serviceRecordPeopleAdapter(detail.linkedServiceRecordId);
  }
  return workflowRecordPeopleAdapter(detail.kind, detail.id);
}

/** NumbersEditor reads a Data Needed item; this is the live record wearing
 *  that shape. Only the five fields it actually reads carry real values. */
function judgmentItemFor(detail: TypeItInRecord, fix: ResolvedTypeItIn): MyJudgmentItem {
  const itemType: JudgmentItemType =
    detail.kind === "opportunity" ? "opportunity_numbers" : "transaction_numbers";
  return {
    issue_id: "",
    item_type: itemType,
    routed_to: null,
    label: fix.title,
    question: fix.title,
    explanation: "",
    company_slug: detail.companySlug,
    record: { kind: detail.kind, id: detail.id, label: fix.record.label },
    household_members: null,
    numbers: detail.numbers,
    record_context:
      detail.realtyCommission || detail.closedOn
        ? {
            property_address: null,
            property_link_missing: false,
            closed_on: detail.closedOn,
            started_on: null,
            stage: null,
            company_record_count: 0,
            realty_commission: detail.realtyCommission,
            sale_price: null,
            advisor_name: null,
          }
        : null,
    fit_context: null,
    first_detected_at: new Date().toISOString(),
  };
}

// ─── Type it in: the detector named the fields ──────────────────────
//
// Built from docs/design/data-needed-overhaul-mockup-v1.html (approved
// 2026-09-30), the "Type it in" card: each field by its plain name, what
// is on file now, the control, Save off until something changed, one
// line saying what the save touches. The fields and their options come
// from the detector's declaration (shared/cleanup/fix.ts); the save goes
// through the record's own endpoint named by the writer
// (shared/cleanup/answer-writers.ts). No field definition lives here.

function FieldsCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedTypeItInFields }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const writer = ANSWER_WRITERS[fix.writer];
  // Only the fields the person touched are sent, so an already-answered
  // field left alone is never re-written (and never audited as a change).
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const touched = Object.keys(answers).filter(
    (k) => answers[k] !== currentAsControlValue(fix.fields.find((f) => f.key === k)!),
  );
  const missingCount = fix.fields.filter((f) => f.current === null).length;

  function set(field: CleanupAskedField, value: string) {
    setAnswers((a) => ({ ...a, [field.key]: value }));
    setNote(null);
  }

  async function runAlternative(a: CleanupFieldsAlternative) {
    if (busy) return;
    setBusy(true);
    setSaveErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: a.verb,
        ...(a.params ? { params: a.params } : {}),
        ...(a.reasonText ? { reason_text: a.reasonText } : {}),
      });
      const undo = res?.undo;
      toast.success(
        a.done,
        undo?.available
          ? {
              action: {
                label: "Undo",
                onClick: () => {
                  void api
                    .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                    .then(() => toast.success("Back on your list."))
                    .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
                },
              },
            }
          : undefined,
      );
      onResolved();
    } catch (e) {
      setSaveErr(e instanceof ApiError ? e.message : "That didn't go through — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (touched.length === 0 || busy) return;
    setBusy(true);
    setSaveErr(null);
    setNote(null);
    try {
      const sent: Record<string, string> = {};
      for (const k of touched) sent[k] = answers[k]!;
      // What was on file, so Undo can put it back through the same save.
      const before: Record<string, string> = {};
      for (const k of touched) before[k] = currentAsControlValue(fix.fields.find((x) => x.key === k)!);
      await api.answerCleanupFields(fix.writer, fix.writerId, fix.fields, sent);
      let closedHere = false;
      if (fix.closeOnSave) {
        // This item's check can't re-run for one record, so the save
        // closes it, recording what was done.
        try {
          await api.decideCleanupIssue({
            issue_id: issue.id,
            verb: "verify",
            reason_text: `Updated on the card: ${touched.map((k) => fix.fields.find((x) => x.key === k)!.label).join(", ")}`,
          });
          closedHere = true;
        } catch {
          /* the save stands; the next sweep clears the item */
        }
      }
      toast.success("Saved.", {
        action: {
          label: "Undo",
          onClick: () => {
            void (async () => {
              try {
                await api.answerCleanupFields(fix.writer, fix.writerId, fix.fields, before);
                if (closedHere) {
                  await api.undoCleanupDecision({ issue_id: issue.id, kind: "reopen", refs: {} });
                }
                toast.success("Put back.");
              } catch {
                toast.error("Couldn't undo. The record's History can roll it back.");
              }
            })();
          },
        },
      });
      if (closedHere) {
        onResolved();
        return;
      }
      // The endpoint clears the item itself once the ask is complete
      // (shared/db/nmls-fields-clear.ts). Ask the issue rather than guess.
      try {
        const res = await api.getCleanupIssue(issue.id);
        if (!CARD_OPEN_STATES.includes(res.issue.state)) {
          onResolved();
          return;
        }
      } catch {
        /* showing the card again is the safe answer */
      }
      setNote("Saved. The rest of the answers are still needed to clear this.");
    } catch (e) {
      setSaveErr(e instanceof ApiError ? e.message : "That didn't save — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">What's missing</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>

      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it happened.</b> {issue.howItHappened}
        </p>
      )}

      <div className="ic-fix">
        <div className="ic-eyebrow">
          The fix · type it in <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <div className="ic-fields">
          {fix.fields.map((f) => {
            const words = currentAsWords(f);
            const value = answers[f.key] ?? currentAsControlValue(f);
            const id = `ic-field-${issue.id}-${f.key}`;
            return (
              <div className="ic-field" key={f.key}>
                <label className="ic-field-k" htmlFor={id}>
                  {f.label}
                  {f.hint && (
                    <span className="ic-field-hint" title={f.hint} aria-label={f.hint}>
                      ?
                    </span>
                  )}
                </label>
                <div className={`ic-field-now${words === null ? " is-gap" : " is-set"}`}>
                  {words === null ? "Not answered" : `On file: ${words}`}
                </div>
                {f.input === "select" && (
                  <select
                    id={id}
                    className="ic-input ic-field-ctl"
                    value={value}
                    disabled={busy}
                    onChange={(e) => set(f, e.target.value)}
                  >
                    <option value="">Choose…</option>
                    {(f.options ?? []).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                )}
                {f.input === "yesno" && (
                  <div className="ic-seg" role="group" aria-labelledby={id}>
                    {(["1", "0"] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={`ic-btn ic-btn--ghost${value === v ? " is-on" : ""}`}
                        aria-pressed={value === v}
                        disabled={busy}
                        onClick={() => set(f, v)}
                      >
                        {v === "1" ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                )}
                {(f.input === "date" || f.input === "text" || f.input === "email" || f.input === "phone") && (
                  <input
                    id={id}
                    className="ic-input ic-field-ctl"
                    type={f.input === "date" ? "date" : f.input === "email" ? "email" : f.input === "phone" ? "tel" : "text"}
                    value={value}
                    disabled={busy}
                    onChange={(e) => set(f, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="ic-btns">
          <button
            type="button"
            className="ic-btn ic-btn--primary"
            data-action="primary"
            disabled={busy || touched.length === 0}
            onClick={() => void save()}
          >
            <Check {...ICON} aria-hidden />
            {touched.length === 0
              ? `Save ${missingCount === 1 ? "the answer" : "answers"}`
              : `Save ${touched.length === 1 ? "1 answer" : `${touched.length} answers`}`}
          </button>
          {fix.alternatives.map((a) => (
            <button
              key={a.label}
              type="button"
              className="ic-btn ic-btn--ghost"
              disabled={busy || disposition.busy}
              title={a.consequence}
              onClick={() => void runAlternative(a)}
            >
              {a.label}
            </button>
          ))}
        </div>
        {fix.alternatives.length > 0 && (
          <p className="ic-meta">{fix.alternatives.map((a) => `${a.label}: ${a.consequence}`).join(" ")}</p>
        )}
        <p className="ic-note">
          Writes {writer.writes}, the same as typing it on the record page. Every change is
          logged with who made it; type a different answer and the new one stands.
        </p>
        {note && <p className="ic-note ic-note--ok">{note}</p>}
        {saveErr && <p className="ic-err">{saveErr}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Pick one: a decision about something outside the OS ───────────
//
// Built from docs/design/data-needed-overhaul-mockup-v1.html, the "Pick
// one" shape: two to four answers, each saying in one line what recording
// it does. The OS is not the system of record for what these cards ask
// about (a custodian's fee, a commission disbursement, a QuickBooks
// account, a mailbox subscription), so there is nothing on a record to
// change. The card asks the question, takes the answer with a reason when
// the answer needs one, records who said what, and keeps the real action
// one labelled click away. It never pretends to have fixed anything.

function PickOneCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedPickOne }) {
  const toast = useToast();
  const disposition = useDisposition(issue, onResolved);
  const pill = confidencePill(fix.confidence);
  const [picked, setPicked] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const option = fix.options.find((o) => o.value === picked) ?? null;
  const needsNote = option?.needsNote === true;
  const askDate = option?.askDate ?? null;
  const ready =
    option !== null &&
    (!needsNote || note.trim().length > 0) &&
    (askDate === null ||
      /^\d{4}-\d{2}-\d{2}$/.test(date) ||
      (askDate.optional === true && date === ""));
  // Some answers change a record (the cancelled-loan card archives a loan
  // or clears its date); most only record who answered. The note under
  // the button says which, for the answer actually picked.
  const anyChanges = fix.options.some((o) => o.changesRecord === true);

  async function record() {
    if (!option || !ready || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: "verify",
        params: {
          answer: option.value,
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(askDate && date ? { date } : {}),
        },
      });
      if (res?.undo?.available) {
        const undo = res.undo;
        toast.success(`Recorded: ${option.label}.`, {
          action: {
            label: "Undo",
            onClick: () => {
              void api
                .undoCleanupDecision({ issue_id: issue.id, kind: undo.kind, refs: undo.refs })
                .then(() => toast.success("Back on your list."))
                .catch(() => toast.error("Couldn't undo. Reopen it from Recently resolved."));
            },
          },
        });
      }
      onResolved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="ic-card">
      <div className="ic-eyebrow">A decision for you</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>

      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}

      <div className="ic-fix">
        <div className="ic-eyebrow">
          The answer <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <ul className="ic-keep ic-pick" role="radiogroup" aria-label={fix.title}>
          {fix.options.map((o) => (
            <li key={o.value} className={picked === o.value ? "is-on" : undefined}>
              <label>
                <input
                  type="radio"
                  name={`ic-pick-${issue.id}`}
                  value={o.value}
                  checked={picked === o.value}
                  disabled={busy}
                  onChange={() => {
                    setPicked(o.value);
                    // Start from what the record already says, if anything.
                    setDate(o.askDate?.current ?? "");
                  }}
                />
                <span style={{ minWidth: 0 }}>
                  <span className="ic-keep-n" style={{ display: "block" }}>
                    {o.label}
                  </span>
                  <span className="ic-keep-m" style={{ display: "block" }}>
                    {o.consequence}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        {askDate && (
          <>
            <label className="ic-field-k" htmlFor={`ic-date-${issue.id}`}>
              {askDate.label}
            </label>
            <input
              id={`ic-date-${issue.id}`}
              type="date"
              className="ic-input"
              value={date}
              disabled={busy}
              onChange={(e) => setDate(e.target.value)}
            />
          </>
        )}
        {needsNote && (
          <>
            <label className="ic-field-k" htmlFor={`ic-note-${issue.id}`}>
              Why
            </label>
            <textarea
              id={`ic-note-${issue.id}`}
              className="ic-input"
              rows={2}
              maxLength={500}
              value={note}
              disabled={busy}
              placeholder="A short reason, so the next person knows"
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        )}
        <div className="ic-btns">
          <button
            type="button"
            className="ic-btn ic-btn--primary"
            data-action="primary"
            disabled={!ready || busy}
            onClick={() => void record()}
          >
            <Check {...ICON} aria-hidden />
            {option ? "Record this answer" : "Pick an answer"}
          </button>
          {fix.link && (
            <a className="ic-btn ic-btn--ghost" href={fix.link.path} target="_blank" rel="noreferrer">
              {fix.link.label} <ExternalLink {...ICON_XS} aria-hidden />
            </a>
          )}
        </div>
        <p className="ic-note">
          {option?.changesRecord
            ? "Makes the change described above and records who answered. You can undo for 60 seconds."
            : anyChanges
              ? "Records your answer and who gave it. Pick an answer to see exactly what it changes. You can undo for 60 seconds."
              : "Records your answer and who gave it. Nothing on any record changes; the real action happens where the link goes. You can undo for 60 seconds."}
        </p>
        {err && <p className="ic-err">{err}</p>}
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

// ─── Nothing to change ──────────────────────────────────────────────

// ─── Split a holding household (2026-10-04) ─────────────────────────
//
// Ported from docs/design/data-needed-split-accounts-mockup-v1.html: one
// row per account with its own "Goes to" answer, the Now and After box,
// one button that says the count, the fine print, and "Show how we read
// it" (closed by default). The rules (labels, counts, what is sent) live
// in split-accounts.ts so they are tested without a browser. The decide
// route re-reads every account and target; nothing here is trusted.

function SplitAccountsCard({
  issue: issueProp,
  fix: fixProp,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedSplitAccounts }) {
  const toast = useToast();
  // After a partial split the item stays open with the accounts that are
  // left; the card re-reads it and keeps going rather than leaving the list.
  const [live, setLive] = useState<{ issue: CleanupIssueSummary; fix: ResolvedSplitAccounts }>({
    issue: issueProp,
    fix: fixProp,
  });
  const { issue, fix } = live;
  const disposition = useDisposition(issue, onResolved);
  const [choices, setChoices] = useState<Record<string, SplitRowChoice>>(() =>
    Object.fromEntries(fix.accounts.map((a) => [a.portfolioId, initialChoice(a)])),
  );
  const [searchFor, setSearchFor] = useState<string | null>(null);
  const [whyOpen, setWhyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [undoBar, setUndoBar] = useState<{ text: string; kind: string; refs: Record<string, unknown>; until: number } | null>(
    null,
  );
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!undoBar) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [undoBar]);

  const summary = summarize(fix.accounts, choices);
  const total = fix.accounts.reduce((s, a) => s + a.valueCents, 0);
  const holdingName = fix.holding.label;

  function setChoice(portfolioId: string, patch: Partial<SplitRowChoice>) {
    setChoices((prev) => ({ ...prev, [portfolioId]: { ...(prev[portfolioId] ?? { value: "", found: null, zip: "", bring: false }), ...patch } }));
  }

  function onSelect(a: CleanupSplitAccount, value: string) {
    if (value === "found") {
      setChoice(a.portfolioId, { value, found: null });
      setSearchFor(a.portfolioId);
      return;
    }
    // "Its own new household" brings the named person by default (see
    // initialChoice); into a family the person stays unless ticked.
    setChoice(a.portfolioId, { value, found: null, bring: value === "new" && a.person != null });
    if (searchFor === a.portfolioId) setSearchFor(null);
  }

  async function reloadAfterPartial() {
    try {
      const res = await api.getCleanupIssue(issue.id);
      const next = resolveFix(res.issue);
      if (next && next.kind === "split-accounts" && res.issue.state !== "resolved") {
        setLive({ issue: res.issue, fix: next });
        setChoices(Object.fromEntries(next.accounts.map((a) => [a.portfolioId, initialChoice(a)])));
        return;
      }
    } catch {
      // Fall through: the list re-reads the item on its next load.
    }
    onResolved();
  }

  async function undo(kind: string, refs: Record<string, unknown>) {
    try {
      await api.undoCleanupDecision({ issue_id: issue.id, kind, refs });
      toast.success("Put back. Every account is where it was.");
      setUndoBar(null);
      await reloadAfterPartial();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Couldn't undo. Each account's History tab can still roll it back.");
    }
  }

  async function go() {
    if (busy || !summary.canGo) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.decideCleanupIssue({
        issue_id: issue.id,
        verb: "split-accounts",
        params: { accounts: answersFor(fix.accounts, choices) },
        // The canonical code for cleanup work; the item's tier asks for one.
        reason_code: "data_quality_cleanup",
      });
      const moved = (
        res as { split?: { moved?: { last_four: string | null; to_label: string }[]; left?: number; people_archived?: number } }
      ).split;
      const n = moved?.moved?.length ?? summary.move;
      const where = (moved?.moved ?? [])
        .map((m) => `${m.last_four ? `····${m.last_four}` : "an account"} to ${m.to_label}`)
        .join(", ");
      const archived = moved?.people_archived ?? 0;
      const text = `Moved ${n} account${n === 1 ? "" : "s"}.${where ? ` ${where}.` : ""} Client status updated.${
        archived > 0
          ? ` ${archived === 1 ? "One person who stayed behind was" : `${archived} people who stayed behind were`} archived with the empty holding household (Undo brings them back).`
          : ""
      }`;
      const left = moved?.left ?? 0;
      if (left > 0 && res.undo?.available) {
        setUndoBar({ text, kind: res.undo.kind, refs: res.undo.refs, until: Date.now() + 60_000 });
        await reloadAfterPartial();
      } else {
        if (res.undo?.available) {
          const handle = res.undo;
          toast.success(text, { action: { label: "Undo", onClick: () => void undo(handle.kind, handle.refs) } });
        } else {
          toast.success(text);
        }
        onResolved();
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "That didn't go through. Nothing moved; try again.");
    } finally {
      setBusy(false);
    }
  }

  const secondsLeft = undoBar ? Math.max(0, Math.ceil((undoBar.until - nowMs) / 1000)) : 0;

  return (
    <article className="ic-card">
      {undoBar && secondsLeft > 0 && (
        <div className="ic-undo" role="status">
          <span>
            <b>{undoBar.text.split(".")[0]}.</b>
            {undoBar.text.slice(undoBar.text.indexOf(".") + 1)}
          </span>
          <button type="button" className="ic-btn ic-btn--ghost ic-btn--sm ic-btn--auto" onClick={() => void undo(undoBar.kind, undoBar.refs)}>
            Undo · {secondsLeft}s
          </button>
        </div>
      )}
      <div className="ic-eyebrow">
        {fix.title} <Pill tone="info">Pick which</Pill>
      </div>
      <h2 className="ic-q">
        {fix.accounts.length === 1 ? "One account is still here. Where does it go?" : issue.decisionQuestion}
      </h2>
      <p className="ic-split-ctx">
        <b>Financial</b> · {fix.accounts.length} account{fix.accounts.length === 1 ? "" : "s"}
        {fix.moreAccounts ? ` shown, ${fix.moreAccounts} more waiting (they appear once these move)` : ""} ·{" "}
        <b className="ic-num">{dollarsWhole(total)}</b> · holding household {holdingName}
      </p>
      <p className="ic-split-why">
        The titles name different owners, so no single family fits. We checked each title against your households.
      </p>

      <div className="ic-split-rows">
        {fix.accounts.map((a) => {
          const c = choices[a.portfolioId] ?? initialChoice(a);
          const st = rowState(c);
          const suggested =
            st === "chosen" &&
            ((a.suggestion?.to === "family" && c.value === `family:${a.suggestion.householdId}`) ||
              (a.suggestion?.to === "new" && c.value === "new"));
          const tag = st === "needs" ? "Needs you" : st === "stay" ? "Stays here" : suggested ? "Suggested" : "Chosen";
          const newName = newHouseholdLabel(a);
          const selectId = `ic-split-${issue.id}-${a.portfolioId}`;
          return (
            <div
              key={a.portfolioId}
              className={`ic-acct ${st === "needs" ? "is-needs" : st === "stay" ? "is-stay" : "is-suggested"}`}
            >
              <div>
                <div className="ic-acct-t">{a.title}</div>
                <div className="ic-acct-m">
                  Account {a.lastFour ? `····${a.lastFour}` : ""} · <b className="ic-num">{dollarsWhole(a.valueCents)}</b>
                  {a.kindLabel ? ` · ${a.kindLabel}` : ""}
                </div>
                {a.read && <SplitReadLine account={a} />}
              </div>
              <div className="ic-acct-ans">
                <label className="ic-acct-k" htmlFor={selectId}>
                  Goes to <span>{tag}</span>
                </label>
                <select
                  id={selectId}
                  className={st === "needs" ? "is-empty" : undefined}
                  value={c.value}
                  disabled={busy}
                  aria-label={`Where account ${a.lastFour ?? a.title} goes`}
                  onChange={(e) => onSelect(a, e.target.value)}
                >
                  {(c.value === "" || a.suggestion === null) && <option value="">Choose where it goes…</option>}
                  {a.families.map((f) => (
                    <option key={f.householdId} value={`family:${f.householdId}`}>
                      {f.label}
                    </option>
                  ))}
                  <option value="found">{c.found ? c.found.label : a.families.length > 0 ? "Find a different family…" : "Find a family…"}</option>
                  {newName && <option value="new">Its own new household: {newName}</option>}
                  <option value="leave">Leave it here for now</option>
                </select>
                {c.value === "found" && searchFor === a.portfolioId && (
                  <SplitFamilySearch
                    holdingId={fix.holding.id}
                    busy={busy}
                    onPick={(h) => {
                      setChoice(a.portfolioId, { value: "found", found: h });
                      setSearchFor(null);
                    }}
                  />
                )}
                {c.value === "found" && c.found && searchFor !== a.portfolioId && (
                  <button type="button" className="ic-lnk" onClick={() => setSearchFor(a.portfolioId)}>
                    Search again
                  </button>
                )}
                {c.value === "new" && (
                  <label className="ic-acct-zip">
                    <span>Zip (optional)</span>
                    <input
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={5}
                      value={c.zip}
                      disabled={busy}
                      onChange={(e) => setChoice(a.portfolioId, { zip: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                      placeholder="5 digits"
                    />
                  </label>
                )}
                {a.person && st === "chosen" && (
                  <label className="ic-acct-bring">
                    <input
                      type="checkbox"
                      checked={c.bring}
                      disabled={busy}
                      onChange={(e) => setChoice(a.portfolioId, { bring: e.target.checked })}
                    />
                    <span>Bring {a.person.name} along</span>
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="ic-after">
        <div>
          <span className="ic-after-k">Now</span>
          {holdingName} (holding household)
          <br />
          <b className="ic-num">
            {fix.accounts.length} account{fix.accounts.length === 1 ? "" : "s"} · {dollarsWhole(total)}
          </b>
        </div>
        <div className="ic-after-arrow" aria-hidden>
          <span>→</span>
        </div>
        <div className="ic-after-to">
          <span className="ic-after-k">After</span>
          {summary.after.length > 0 && (
            <ul>
              {summary.after.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          <small>{summary.leftLine}</small>
        </div>
      </div>

      {whyOpen && (
        <div className="ic-split-howread">
          <div>
            <h4>How we read each title</h4>
            <ul>
              <li>The account title is split into owner names (trustee, grantor, "FBO").</li>
              <li>Each name is matched to your households by name, then confirmed by address or zip.</li>
              <li>Only one strong match counts as a suggestion. Two possible matches are shown and left blank.</li>
            </ul>
          </div>
          <div>
            <h4>What the move does</h4>
            <ul>
              <li>Each account moves as its own record, with its history and any care plan.</li>
              <li>Client status is recalculated for both households, so a family never reads as a Financial client without an account.</li>
              <li>Every move is one version and one audit row. One Undo brings all of them back.</li>
            </ul>
          </div>
        </div>
      )}

      <div className="ic-fix">
        <button
          type="button"
          className="ic-btn ic-btn--primary"
          data-action="primary"
          disabled={busy || !summary.canGo}
          onClick={() => void go()}
        >
          {busy ? "Moving…" : summary.label}
        </button>
        <p className="ic-split-fine">{summary.fine}</p>
        {err && <p className="ic-err">{err}</p>}
        <button type="button" className="ic-lnk ic-lnk--quiet" onClick={() => setWhyOpen((v) => !v)}>
          {whyOpen ? "Hide how we read it" : "Show how we read it"}
        </button>
      </div>

      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy || busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

/** How we read the title, drawn the way the mockup draws it: the names the
 *  title reads in bold, then the verdict in green when there is a
 *  suggestion and amber when only a person can say. */
function SplitReadLine({ account }: { account: CleanupSplitAccount }) {
  const text = account.read;
  const cut = text.indexOf(". ");
  const lead = cut >= 0 && text.startsWith("Title names") ? text.slice(0, cut + 1) : "";
  const rest = lead ? text.slice(cut + 2) : text;
  const names = account.names.filter((n) => n && lead.includes(n));
  const parts: (string | { b: string })[] = [];
  let tail = lead;
  for (const n of names) {
    const i = tail.indexOf(n);
    if (i < 0) continue;
    parts.push(tail.slice(0, i), { b: n });
    tail = tail.slice(i + n.length);
  }
  parts.push(tail);
  // Green only when the title matched a family; everything else is a
  // caution, as the drawing marks it.
  const tone = account.suggestion?.to === "family" ? "is-good" : "is-warn";
  return (
    <div className="ic-acct-read">
      {lead &&
        parts.map((p, i) => (typeof p === "string" ? <span key={i}>{p}</span> : <b key={i}>{p.b}</b>))}
      {lead && " "}
      <span className={tone}>{rest}</span>
    </div>
  );
}

/** "Find a different family…": a household search under the row. Import
 *  placeholders and archived households are left out (the server refuses
 *  them anyway), and so is the holding household itself. */
function SplitFamilySearch({
  holdingId,
  busy,
  onPick,
}: {
  holdingId: string;
  busy: boolean;
  onPick: (h: { id: string; label: string }) => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchErr(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({ q: q.trim(), limit: 20 });
        if (cancelled) return;
        setResults(
          res.households.filter(
            (h) => h.id !== holdingId && !h.is_quarantined && !h.quarantine_reason && !h.archived_at,
          ),
        );
        setSearchErr(null);
      } catch (e) {
        if (cancelled) return;
        setSearchErr(e instanceof ApiError ? e.message : "Search failed");
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, holdingId]);

  return (
    <div className="ic-acct-search">
      <label className="ic-srch">
        <Search {...ICON} aria-hidden />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search households by name…" autoFocus />
      </label>
      <ol className="ic-res">
        {results.map((h) => {
          const label = h.primary_city ? `${h.display_name} · ${h.primary_city}` : h.display_name;
          return (
            <li key={h.id}>
              <div>
                <div className="ic-res-n">{h.display_name}</div>
                <div className="ic-res-m">
                  {[h.members_label, h.primary_city && h.primary_state ? `${h.primary_city}, ${h.primary_state}` : null]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <button
                type="button"
                className="ic-btn ic-btn--ghost ic-btn--sm"
                disabled={busy}
                onClick={() => onPick({ id: h.id, label })}
              >
                Goes here
              </button>
            </li>
          );
        })}
      </ol>
      {searching && <p className="ic-res-empty">Searching…</p>}
      {searchErr && <p className="ic-err">{searchErr}</p>}
      {!searching && q.trim().length >= 2 && results.length === 0 && !searchErr && (
        <p className="ic-res-empty">No family matches "{q.trim()}".</p>
      )}
    </div>
  );
}

/**
 * The answer lives on one exact page (build step C1): the card says what to
 * do and opens it. It records nothing itself and has no "Seen it": the item
 * clears when that page's own action lands, so work still owed can't be
 * hidden from the list. Parking it ("Later") still works from the footer.
 */
function OpenPageCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps & { fix: ResolvedOpenPage }) {
  const disposition = useDisposition(issue, onResolved);
  return (
    <article className="ic-card">
      <div className="ic-eyebrow">A step for you</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How it came up.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-fix">
        <div className="ic-eyebrow">What to do</div>
        <p className="ic-how" style={{ marginTop: 0 }}>
          {fix.what}
        </p>
        <div className="ic-btns">
          <a className="ic-btn ic-btn--primary" data-action="primary" href={fix.link.path}>
            {fix.link.label} <ExternalLink {...ICON_XS} aria-hidden />
          </a>
        </div>
        <p className="ic-note">This clears itself once it is done there. Nothing is recorded from this card.</p>
      </div>
      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}

function AcknowledgeCard({
  issue,
  fix,
  onOpenRecord,
  onResolved,
  onToggleMore,
  moreOpen,
}: IssueCardProps) {
  const disposition = useDisposition(issue, onResolved);
  const first = issue.affectedRecords[0];
  const href = first ? recordDeepLink(first.kind, first.id) : null;
  return (
    <article className="ic-card">
      <div className="ic-eyebrow">Heads up</div>
      <h2 className="ic-q">{issue.decisionQuestion}</h2>
      {issue.howItHappened && (
        <p className="ic-how">
          <b>How we know.</b> {issue.howItHappened}
        </p>
      )}
      <div className="ic-none">
        <Info {...ICON} aria-hidden />
        <div>
          <b>Nothing to change on the record.</b> This is for you to know. "Seen it" clears it
          from the list and notes who saw it; it changes nothing on the record.
        </div>
      </div>
      <div className="ic-btns">
        <button
          type="button"
          className="ic-btn ic-btn--navy"
          disabled={disposition.busy}
          onClick={() => void disposition.decide("acknowledge")}
        >
          <Check {...ICON} aria-hidden /> Seen it
        </button>
        {href && first && (
          <a className="ic-btn ic-btn--ghost" href={href} target="_blank" rel="noreferrer">
            Open {first.label} <ExternalLink {...ICON_XS} aria-hidden />
          </a>
        )}
      </div>
      {disposition.err && <p className="ic-err">{disposition.err}</p>}
      <WhyWeThinkSo issue={issue} fix={fix} onOpenRecord={onOpenRecord} />
      <CardFooter
        issueId={issue.id}
        busy={disposition.busy}
        onSnooze={() => void disposition.decide("snooze")}
        onDismiss={() => void disposition.decide("dismiss")}
        onResolved={onResolved}
        onToggleMore={onToggleMore}
        moreOpen={moreOpen}
      />
    </article>
  );
}
